import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Input, Badge, Tabs, TabsContent, TabsList, TabsTrigger,
  Separator, DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  Avatar, AvatarFallback
} from "../../components/ui";
import { 
  PlusCircle, Users, Building2, Handshake, Banknote, Search, Mail, Phone, Filter,
  Activity as ActivityIcon, CirclePlus, FileText, MapPin, ArrowUpRight, ChevronRight, CircleHelp,
  Info, Workflow, Target, Briefcase, Bell, ClipboardList, Layers3, FolderKanban, CheckCircle2, Clock3
} from "lucide-react";
import { Drawer } from "../../components/ui/drawer";
import { Modal } from "../../components/ui/modal";
import {
  fetchParties, createParty,
  fetchLeads, createLead, fetchOpportunities, createOpportunity, updateOpportunity, updateLead, convertLead, fetchOpportunity,
  deleteOpportunity, fetchOpportunityNotes, addOpportunityNote, fetchLeadFiles, deleteLeadFile, uploadLeadFiles, fetchLeadNotes, fetchRecentActivities,
  updateContact, createContact, addLeadNote, fetchContactsForParty, updateParty, updateActivity, fetchActivitiesForParty, createActivityForParty,
  searchAll, fetchAddressesForParty, createAddress, updateAddress, deleteAddress, fetchChannelsForParty, createChannel, updateChannel, deleteChannel,
  fetchBankAccountsForParty, createBankAccount, updateBankAccount, deleteBankAccount, fetchIdentifiersForParty, createIdentifier, updateIdentifier, deleteIdentifier,
  fetchCampaigns, createCampaign, updateCampaign, fetchCampaignTokens, createCampaignToken, revokeToken, fetchOpportunityDetailsForCrm
} from "../../services/crm";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import BigModal from "../../components/BigModal";
import { authHeaders } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import {
  fetchPipelineStages,
  fetchChannels as fetchChannelSettings,
  fetchLeadStages,
} from "../../services/crmSettings";
import { getAuth } from "firebase/auth";
import axios from "../../utils/axios";

/**
 * CRM Main Page – v0.1 (skeleton)
 *
 * ✔ Jedna ulazna tačka za sve module: Subjekti, Kontakti, Aktivnosti, Prodaja (Leads/Opportunities), Kampanje
 * ✔ Globalna pretraga (kasnije vezati na FTS / PartySearch)
 * ✔ Brzi dodaci (Party, Aktivnost, Lead)
 * ✔ KPI kartice (dinamički)
 * ✔ Tabs po ulozi subjekata (Customers/Suppliers/Banks/Partners)
 * ✔ Sekcije: Recent Activities, Pipeline, Quick Links
 * ✔ Čisti Tailwind + shadcn/ui + lucide-react
 *
 * TODO (sledeći koraci):
 * - Povezati sa API-jem (Dapper/REST) za Party/Activity/Lead
 * - Dodati modal forme za Quick Add dugmad
 * - Dodati RBAC (Owner/Team), Tenant filter i User avatar sa menijem
 * - Dodati Timeline 360° za selektovani Party
 */
// --- Pomoćne komponente ---

const FALLBACK_OPPORTUNITY_STAGES = [
  { code: "qualification", label: "Qualification", isWon: false, isLost: false },
  { code: "proposal", label: "Proposal", isWon: false, isLost: false },
  { code: "negotiation", label: "Negotiation", isWon: false, isLost: false },
  { code: "won", label: "Won", isWon: true, isLost: false },
  { code: "lost", label: "Lost", isWon: false, isLost: true },
];

const FALLBACK_ACTIVITY_CHANNELS = [
  { code: "Email", label: "Email" },
  { code: "PhoneCall", label: "Phone Call" },
  { code: "Meeting", label: "Meeting" },
  { code: "Task", label: "Task" },
];

const FALLBACK_LEAD_STAGES = [
  { code: "new", label: "New", isWon: false, isLost: false },
  { code: "contacted", label: "Contacted", isWon: false, isLost: false },
  { code: "qualified", label: "Qualified", isWon: false, isLost: false },
  { code: "won", label: "Won", isWon: true, isLost: false },
  { code: "lost", label: "Lost", isWon: false, isLost: true },
  { code: "disqualified", label: "Disqualified", isWon: false, isLost: true },
];

// Komponente bez tip anotacija
function KpiCard({ title, value, delta, hint }) {
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {delta && <div className="mb-1">↗ {delta}</div>}
        {hint && <div className="text-gray-500 dark:text-neutral-400">{hint}</div>}
      </CardContent>
    </Card>
  );
}



function RoleBadge({ code }) {
  const map = {
    Customer: { label: "Customer" },
    Supplier: { label: "Supplier" },
    Bank: { label: "Bank" },
    Partner: { label: "Partner" },
  };
  return <Badge variant="secondary">{map[code]?.label || code}</Badge>;
}

function PartyItem({ p, onOpen }) {
  const roles = Array.isArray(p?.roleCodes) ? p.roleCodes : [];
  const initials = (p?.displayName || "")
    .split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

  return (
    <button onClick={() => onOpen?.(p)} className="w-full text-left">
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback>{initials || "?"}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{p.displayName}</div>
          <div className="text-xs text-muted-foreground truncate">
            {p.city || "—"}{p.country ? `, ${p.country}` : ""}
          </div>
        </div>
        <div className="hidden md:flex gap-2">
          {roles.map(rc => <RoleBadge key={rc} code={rc} />)}
        </div>
      </div>
    </button>
  );
}


function ActivityRow({ a, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen?.(a)}
      className="w-full text-left flex items-start justify-between py-2 hover:bg-slate-50 dark:hover:bg-neutral-800 rounded-md px-2"
    >
      <div className="min-w-0">
        <div className="font-medium leading-tight truncate">{a.subject}</div>
        <div className="text-xs text-gray-500 dark:text-neutral-400 truncate">
          {a.withParty} • {a.when}
        </div>
      </div>
      <Badge variant={a.status === "open" ? "default" : a.status === "done" ? "secondary" : "outline"}>
        {a.status}
      </Badge>
    </button>
  );
}



function PipelineCard({ opp, onUpdateStage, onOpen, stageOptions, t }) {
  const fmt = (amt, cur) =>
    amt == null || amt === ""
      ? "—"
      : new Intl.NumberFormat("sr-RS", { style: "currency", currency: cur || "EUR" })
          .format(Number(amt));

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader
        className="pb-2 cursor-pointer"
        onClick={() => onOpen?.(opp.id)}
        role="button"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{opp.name}</CardTitle>
            <CardDescription>{opp.account}</CardDescription>
          </div>

          <select
            className="h-8 rounded-md border px-2 text-xs bg-white dark:bg-neutral-900"
            value={opp.stage || stageOptions[0]?.code || "qualification"}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onUpdateStage?.(e.target.value)}
            title="Promeni fazu"
          >
           {stageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.oppForm.stages.${s.code}`, s.label || s.code)}
  </option>
))}
          </select>
        </div>
      </CardHeader>

      <CardContent className="text-sm flex items-center justify-between">
        <div>
          <div className="font-semibold">{fmt(opp.amount, opp.currency)}</div>
          <div className="text-gray-500 dark:text-neutral-400">
            {opp.closeDate ? `Close: ${opp.closeDate}` : "—"}
          </div>
        </div>

        <Button variant="ghost" size="sm" onClick={() => onOpen?.(opp.id)}>
          Otvori
        </Button>
      </CardContent>
    </Card>
  );
}





// --- Glavna stranica ---

export default function CRMHome() {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("All");
  const { t, i18n } = useTranslation();

  const [infoOpen, setInfoOpen] = useState(false);
const [infoTab, setInfoTab] = useState("overview");
  

  const [parties, setParties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ displayName: "", roleCodes: ["Customer"], city: "", country: "RS", tags: [] });

  const [activities, setActivities] = useState([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
const [activitiesOffset, setActivitiesOffset] = useState(0);
const [activitiesHasMore, setActivitiesHasMore] = useState(false);
const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);

  const [contacts, setContacts] = useState([]);
const [newC, setNewC] = useState({
  partyId: null,
  fullName: "",
  email: "",
  phone: "",
  title: "",
  notes: ""
});

const navigate = useNavigate();

  const [actOpen, setActOpen] = useState(false);
  const [actPartyLocked, setActPartyLocked] = useState(false);
const [actForm, setActForm] = useState({
  partyId: null,
  subject: "",
  channel: "Email",
  when: "",
  withParty: "",
  status: "open",
  notes: ""
});
const [leads, setLeads] = useState([]);
const [leadOpen, setLeadOpen] = useState(false);
const [leadForm, setLeadForm] = useState({
  partyName: "",
  contactName: "",   // NOVO
  phone: "",         // NOVO
  email: "",         // opciono
  source: "Manual",
  score: 0,
  stage: "new",
  notes: ""
});
const [opps, setOpps] = useState([]);
const [pipelineStages, setPipelineStages] = useState([]);
const [activityChannels, setActivityChannels] = useState([]);
const [leadStages, setLeadStages] = useState([]);
const [oppOpen, setOppOpen] = useState(false);
const [oppForm, setOppForm] = useState({
  name: "",
  account: "",
  partyId: null,
  amount: "",
  currency: "EUR",
  stage: "qualification",
  closeDate: "",
  notes: ""
});

// ➕ NEW
const [activityDetails, setActivityDetails] = useState(null);   // jedan item
const [allActivitiesOpen, setAllActivitiesOpen] = useState(false);

// ✚ EDIT modal state
const [activityEdit, setActivityEdit] = useState(null);
const [activitySaving, setActivitySaving] = useState(false);

// filter koji utiče na prikaz pipeline liste
const [pipelineStageFilter, setPipelineStageFilter] = useState("");

const stageOptions = useMemo(() => {
  if (pipelineStages.length) {
    return pipelineStages
      .filter((x) => Number(x.is_active) === 1 || x.is_active === true)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
      .map((x) => ({
        code: x.code,
        label: x.label || x.code,
        isWon: !!x.is_won,
        isLost: !!x.is_lost,
      }));
  }

  return FALLBACK_OPPORTUNITY_STAGES;
}, [pipelineStages]);

const channelOptions = useMemo(() => {
  if (activityChannels.length) {
    return activityChannels
      .filter((x) => Number(x.is_active) === 1 || x.is_active === true)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
      .map((x) => ({
        code: x.code,
        label: x.label || x.code,
      }));
  }

  return FALLBACK_ACTIVITY_CHANNELS;
}, [activityChannels]);

const defaultActivityChannel = channelOptions[0]?.code || "Email";

const leadStageOptions = useMemo(() => {
  if (leadStages.length) {
    return leadStages
      .filter((x) => Number(x.is_active) === 1 || x.is_active === true)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
      .map((x) => ({
        code: x.code,
        label: x.label || x.code,
        isWon: !!x.is_won,
        isLost: !!x.is_lost,
      }));
  }

  return FALLBACK_LEAD_STAGES;
}, [leadStages]);

const defaultLeadStage =
  leadStageOptions.find((x) => !x.isWon && !x.isLost)?.code || "new";

// lista vidljivih opportunity-ja po filteru
const defaultOpportunityStage =
  stageOptions.find((x) => !x.isWon && !x.isLost)?.code || "qualification";

const visibleOpps = useMemo(() => {
  if (!pipelineStageFilter) return opps;
  return opps.filter((o) => (o.stage || defaultOpportunityStage) === pipelineStageFilter);
}, [opps, pipelineStageFilter, defaultOpportunityStage]);

const stageCurrencyTotals = useMemo(() => {
  const acc = {};
  for (const o of opps) {
    const s = o.stage || defaultOpportunityStage;
    const c = o.currency || "EUR";
    const a = Number(o.amount) || 0;

    if (!acc[s]) acc[s] = { count: 0, currency: {} };
    acc[s].count += 1;
    acc[s].currency[c] = (acc[s].currency[c] || 0) + a;
  }
  return acc;
}, [opps, defaultOpportunityStage]);


const [campaignsOpen, setCampaignsOpen] = useState(false);
const [campaigns, setCampaigns] = useState([]);
const [campQ, setCampQ] = useState("");
const [campStatus, setCampStatus] = useState("");
const [selectedCampaign, setSelectedCampaign] = useState(null);
const [tokens, setTokens] = useState([]);


async function openCampaigns() {
  setCampaignsOpen(true);
  const list = await fetchCampaigns({ q: campQ, status: campStatus, limit: 50, offset: 0 });
  setCampaigns(list.items || list); // zavisi šta vraća backend
}

async function openCampaign(c) {
  setSelectedCampaign(c);
  const tk = await fetchCampaignTokens(c.id);
  setTokens(tk.items || tk);
}

async function createProjectFromOpportunity(opportunityId) {
  const headers = await authHeaders();
  const res = await axios.post(
    `https://modularobjects.com/marketing/api/crm/opportunities/${opportunityId}/create-project`,
    {},
    { headers }
  );

  return res.data;
}

// helper za lepo formatiranje totals-a po valuti
function fmtCurrencyTotals(map) {
  if (!map) return "—";
  return Object.entries(map)
    .map(([cur, val]) =>
      new Intl.NumberFormat("sr-RS", { style: "currency", currency: cur }).format(val)
    )
    .join(" • ");
}


// otvaranje editora iz reda (pretvori "YYYY-MM-DD HH:mm" → "YYYY-MM-DDTHH:mm")
function openActivityEditor(a) {
  setActivityEdit({
    id: a.id,
    partyId: a.partyId,
    withParty: a.withParty || "",
    subject: a.subject || "",
    channel: a.channel || "Email",
    when: a.when ? a.when.replace(" ", "T") : "", // za <input type="datetime-local">
    status: a.status || "open",
    notes: a.notes || ""
  });
}


const openActivity = (a) => setActivityDetails(a);
const closeActivity = () => setActivityDetails(null);


// Addresses
const [addresses, setAddresses] = useState([]);
const [addrForm, setAddrForm] = useState({
  line1: "", line2: "", city: "", region: "", postCode: "", country: "RS",
  lat: "", lng: "", kind: "hq", isPrimary: false
});

// Channels
const [channels, setChannels] = useState([]);
const [chForm, setChForm] = useState({ kind: "email", value: "", isPrimary: false });

// Bank accounts
const [banks, setBanks] = useState([]);
const [bankForm, setBankForm] = useState({
  bankName:"", accountNumber:"", iban:"", swift:"", currency:"", isPrimary:false
});

// Identifiers
const [idents, setIdents] = useState([]);
const [idForm, setIdForm] = useState({ kind:"PIB", value:"", issuerCountry:"RS", isPrimary:true });


// ⬇️ dodaj ova dva state-a
const [oppDetailsOpen, setOppDetailsOpen] = useState(false);
const [oppDetails, setOppDetails] = useState(null);

// state
const [stageFilter, setStageFilter] = useState(""); // "", "new", "contacted", ...
const [q, setQ] = useState("");
const [limit] = useState(20);
const [offset, setOffset] = useState(0);

const [loading, setLoading] = useState(false);
const [hasMore, setHasMore] = useState(true);
const [error, setError] = useState("");

const [recentActivities, setRecentActivities] = useState([]);
const [recentActivitiesLimit, setRecentActivitiesLimit] = useState(50);

const [globalResults, setGlobalResults] = useState([]);
const [qGlobal, setQGlobal] = useState("");

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const rows = await fetchLeadStages();
      if (!cancelled) {
        setLeadStages(Array.isArray(rows) ? rows : []);
      }
    } catch (e) {
      console.error("Failed to load lead stages", e);
      if (!cancelled) setLeadStages([]);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const rows = await fetchPipelineStages();
      if (!cancelled) {
        setPipelineStages(Array.isArray(rows) ? rows : []);
      }
    } catch (e) {
      console.error("Failed to load pipeline stages", e);
      if (!cancelled) setPipelineStages([]);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const rows = await fetchChannelSettings();
      if (!cancelled) {
        setActivityChannels(Array.isArray(rows) ? rows : []);
      }
    } catch (e) {
      console.error("Failed to load activity channels", e);
      if (!cancelled) setActivityChannels([]);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  let cancel = false;
  if (!qGlobal || qGlobal.trim().length < 2) { setGlobalResults([]); return; }

  const t = setTimeout(async () => {
    try {
      const items = await searchAll(qGlobal.trim());
      if (!cancel) setGlobalResults(items);
    } catch (e) { if (!cancel) setGlobalResults([]); }
  }, 300);

  return () => { cancel = true; clearTimeout(t); };
}, [qGlobal]);

useEffect(() => {
  const u = new URL(window.location.href);
  const tc = u.searchParams.get("tc");
  if (tc) document.cookie = `mo_track=${encodeURIComponent(tc)}; path=/; max-age=${60*60*24*30}`;
}, []);


useEffect(() => {
  (async () => {
    try {
      const items = await fetchRecentActivities(recentActivitiesLimit);
      setRecentActivities(items);
    } catch (e) {
      console.error(e);
      setRecentActivities([]);
    }
  })();
}, [recentActivitiesLimit]);

// učitaj partije kad se promeni tab ili query
useEffect(() => {
  let cancelled = false;

  async function load() {
    setLoading(true);
    try {
      const role = tab === "All" ? undefined : tab;
      const items = await fetchParties({ q: query, role, limit: 50, offset: 0 });
      if (!cancelled) {
        setParties(items);
      }
    } catch (e) {
      if (!cancelled) {
        console.error(e);
        setError(e.message || "Greška pri učitavanju partija.");
        setParties([]);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
  }

  // debounce za query
  const t = setTimeout(load, 300);
  return () => { cancelled = true; clearTimeout(t); };
}, [tab, query]);  // 👈 reaguj na oba



// load more
async function loadMore() {
  if (loading || !hasMore) return;
  setLoading(true);
  try {
    const items = await fetchLeads({ q, stage: stageFilter, limit, offset });
    setLeads(prev => [...prev, ...items]);          // append
    setOffset(prev => prev + items.length);
    setHasMore(items.length === limit);
  } finally {
    setLoading(false);
  }
}


// ⬇️ funkcija koja učitava detalje i otvara modal
async function openOpportunityDetails(id) {
  try {
    const item = await fetchOpportunityDetailsForCrm(id);
    console.log("CRM OPPORTUNITY DETAILS:", item);
    setOppDetails(item);
    setOppDetailsOpen(true);
  } catch (e) {
    alert("Ne mogu da učitam detalje: " + e.message);
  }
}

async function loadMoreActivities() {
  if (!selected?.id || activitiesLoadingMore || !activitiesHasMore) return;

  try {
    setActivitiesLoadingMore(true);

    const res = await fetchActivitiesForParty(selected.id, {
      limit: 50,
      offset: activitiesOffset
    });

    setActivities((prev) => [...prev, ...res.items]);
    setActivitiesOffset((prev) => prev + res.items.length);
    setActivitiesHasMore(res.hasMore);
    setActivitiesTotal(res.total);
  } catch (e) {
    console.error(e);
    alert(e.message || "Greška pri učitavanju aktivnosti.");
  } finally {
    setActivitiesLoadingMore(false);
  }
}

// 1) sortiraj najsvežije na vrh (po createdAt ako ga ima, fallback na id)
const sortedLeads = React.useMemo(() => {
  return [...leads].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : (a.id ?? 0);
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : (b.id ?? 0);
    return tb - ta; // DESC
  });
}, [leads]);

const [leadDetails, setLeadDetails] = useState(null);

  // init load
  React.useEffect(() => {
  fetchParties().then(setParties);
  fetchOpportunities().then(setOpps);
}, []);

const filteredParties = parties.filter(p => {
  // filter po roli
  if (tab !== "All" && !(p.roleCodes || []).includes(tab)) return false;

  // filter po query
  if (query.trim()) {
    const ql = query.toLowerCase();
    return (
      (p.displayName || "").toLowerCase().includes(ql) ||
      (p.city || "").toLowerCase().includes(ql) ||
      (p.country || "").toLowerCase().includes(ql)
    );
  }

  return true;
});



  // open drawer
  const openParty = async (p) => {
  setSelected(p);
  setDrawerOpen(true);

  // aktivnosti
  const actsRes = await fetchActivitiesForParty(p.id, { limit: 50, offset: 0 });
setActivities(actsRes.items);
setActivitiesTotal(actsRes.total);
setActivitiesOffset(actsRes.items.length);
setActivitiesHasMore(actsRes.hasMore);

  // kontakti
  try {
    const list = await fetchContactsForParty(p.id);
    setContacts(Array.isArray(list) ? list : []);
  } catch { setContacts([]); }

  // addresses
try { setAddresses(await fetchAddressesForParty(p.id)); } catch { setAddresses([]); }
// channels
try { setChannels(await fetchChannelsForParty(p.id)); } catch { setChannels([]); }
// banks
try { setBanks(await fetchBankAccountsForParty(p.id)); } catch { setBanks([]); }
// identifiers
try { setIdents(await fetchIdentifiersForParty(p.id)); } catch { setIdents([]); }


  setNewC((s) => ({ ...s, partyId: p.id })); // 🔴 bitno za kreiranje kontakta
};




// state
const [leadFiles, setLeadFiles] = useState([]);
const [newFiles, setNewFiles] = useState([]);
const [uploading, setUploading] = useState(false);

// helper
function formatKB(bytes) {
  if (!bytes) return "0 KB";
  const kb = Math.round(bytes / 1024);
  return kb >= 1024 ? `${(kb/1024).toFixed(1)} MB` : `${kb} KB`;
}

const fmt = new Intl.NumberFormat("sr-RS", { style: "currency", currency: "EUR" });
const pipelineValue = fmt.format(
  opps.reduce((s, o) => s + (Number(o.amount) || 0), 0)
);

// izračunaj total po valuti
const totalsByCurrency = opps.reduce((acc, o) => {
  const curr = o.currency || "EUR";             // default ako nema
  const amount = Number(o.amount) || 0;
  acc[curr] = (acc[curr] || 0) + amount;
  return acc;
}, {});

// napravi prikaz (npr. array za map)
const pipelineTotals = Object.entries(totalsByCurrency).map(
  ([currency, total]) =>
    new Intl.NumberFormat("sr-RS", {
      style: "currency",
      currency
    }).format(total)
);


// učitaj fajlove kada se otvori modal za određeni lead
useEffect(() => {
  if (!leadDetails?.id) {
    setLeadFiles([]);   // reset kad se modal zatvori
    setNewFiles([]);
    return;
  }
  let alive = true;
  (async () => {
    try {
      const items = await fetchLeadFiles(leadDetails.id);
      if (alive) setLeadFiles(items);
    } catch (e) {
      console.error(e);
    }
  })();
  return () => { alive = false; };
}, [leadDetails?.id]);


  return (
    <div className="p-4 md:p-6 px-3 max-w-[1400px] mx-auto overflow-x-hidden">
      {/* Header */}
<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
  <div>
    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{t("crm.overview")}</h1>
    <p className="text-sm text-muted-foreground">
      {t("crm.overviewDesc")}
    </p>
  </div>

  {/* Dugmad: wrap + responsive širine */}
  <div className="flex flex-wrap gap-2 w-full md:w-auto">

    <Button
  variant="outline"
  className="gap-2 w-full sm:w-auto"
  onClick={() => setInfoOpen(true)}
>
  <Info className="h-4 w-4" /> CRM Guide
</Button>

    <Button className="gap-2 w-full sm:w-auto" onClick={() => setNewOpen(true)}>
      <PlusCircle className="h-4 w-4" /> {t("crm.actions.newParty")}
    </Button>

    

    <Button
  variant="secondary"
  className="gap-2 w-full sm:w-auto"
  onClick={() => {
    setActPartyLocked(false);

    setActForm({
      partyId: null,
      subject: "",
      channel: defaultActivityChannel,
      when: "",
      withParty: "",
      status: "open",
      notes: ""
    });

    setActOpen(true);
  }}
>
      <CirclePlus className="h-4 w-4" /> {t("crm.actions.newActivity")}
    </Button>

    <Button
  variant="outline"
  className="gap-2 w-full sm:w-auto"
  onClick={() => {
    setLeadForm({
  partyName: selected?.displayName || "",
  contactName: "",
  phone: "",
  email: "",
  source: "Manual",
  score: 0,
  stage: defaultLeadStage,
  notes: ""
});
    setLeadOpen(true);
  }}
>
      <FileText className="h-4 w-4" /> {t("crm.actions.newLead")}
    </Button>

    <Button
  variant="outline"
  className="gap-2 w-full sm:w-auto"
  onClick={() => {
    setOppForm({
      name: "",
      account: selected?.displayName || "",
      partyId: selected?.id || null,
      amount: "",
      currency: "EUR",
      stage: defaultOpportunityStage,
      closeDate: "",
      notes: ""
    });
    setOppOpen(true);
  }}
>
      <PlusCircle className="h-4 w-4" /> {t("crm.actions.newOpportunity")}
    </Button>
  </div>
</div>


      {/* Global Search & Filters */}
      <Card className="mt-4">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />
              <Input placeholder={t("crm.searchPlaceholder")} className="pl-9" value={query} onChange={e => {const v = e.target.value;setQuery(v); setQGlobal(v);}} />
              {/* Globalni “dropdown” rezultata */}
{qGlobal.trim().length >= 2 && (
  <div
    className="
      absolute left-0 right-0 mt-2 z-50
      rounded-md border bg-white dark:bg-neutral-900
      border-gray-200 dark:border-neutral-800
      shadow-lg max-h-96 overflow-y-auto
    "
  >
    <div className="p-2 text-xs text-muted-foreground">
      {t("crm.searchResultsPlaceholder")}
    </div>

    <div className="divide-y">
      {globalResults.map(r => (
        <div
          key={`${r.kind}-${r.id}`}
          className="py-2 px-3 hover:bg-slate-50 dark:hover:bg-neutral-800 cursor-pointer"
          onClick={() => {
            if (r.kind === "party") {
              // imamo id + title; dovoljno za tvoj drawer
              openParty({ id: r.id, displayName: r.title });
            } else if (r.kind === "contact") {
              // otvori povezani party
              if (r.partyId) openParty({ id: r.partyId, displayName: r.subtitle || "Party" });
            } else if (r.kind === "lead") {
              setLeadDetails({ id: r.id, partyName: r.title });
            } else if (r.kind === "opportunity") {
              openOpportunityDetails(r.id);
            } else if (r.kind === "activity") {
              if (r.partyId) openParty({ id: r.partyId, displayName: r.subtitle || "Party" });
            }
            setQGlobal(""); // 👈 zatvori dropdown
          }}
        >
          <div className="text-sm font-medium truncate">{r.title}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {r.kind} • {r.subtitle} • {r.whenAt ? new Date(r.whenAt).toLocaleString() : "—"}
          </div>
        </div>
      ))}

      {globalResults.length === 0 && (
        <div className="py-6 px-3 text-center text-muted-foreground text-sm">
          {t("crm.actions.noMore")}
        </div>
      )}
    </div>
  </div>
)}
                        </div>
            <DropdownMenu>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t("crm.roleFilter")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTab("All")}>{t("crm.roles.all")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("Customer")}>{t("crm.roles.customer")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("Supplier")}>{t("crm.roles.supplier")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("Bank")}>{t("crm.roles.bank")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("Partner")}>{t("crm.roles.partner")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
<div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 mt-4">
  <KpiCard
  title={t("crm.kpi.pipeline")}
  value={pipelineTotals.join(" • ")}   // npr. "12.500,00 EUR • 1.200.000,00 RSD • 8,000.00 USD"
/>
{/* Summary by stage */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
  {stageOptions.map((s) => {
  const sum = stageCurrencyTotals[s.code];
  const active = pipelineStageFilter === s.code;

  return (
    <Card
      key={s.code}
      className={`cursor-pointer transition ${active ? "ring-2 ring-blue-500" : ""}`}
      onClick={() => setPipelineStageFilter(active ? "" : s.code)}
      title="Klik za filtriranje"
    >
      <CardHeader className="pb-1">
        <CardDescription className="capitalize">
          {t(`crm.oppForm.stages.${s.code}`, s.label || s.code)}
        </CardDescription>
        <CardTitle className="text-base">
          {fmtCurrencyTotals(sum?.currency)}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {(sum?.count ?? 0)} deal{(sum?.count ?? 0) === 1 ? "" : "s"}
      </CardContent>
    </Card>
  );
})}
</div>

{/* Traka sa filterom */}
<div className="flex items-center justify-between mb-2">
  <div className="text-sm text-muted-foreground">
    {pipelineStageFilter
      ? `Filter: ${pipelineStageFilter}`
      : "Prikaz: svi statusi"}
  </div>
  <div className="flex gap-2">
    <select
      className="h-8 rounded-md border px-2 text-xs bg-white dark:bg-neutral-900"
      value={pipelineStageFilter}
      onChange={(e) => setPipelineStageFilter(e.target.value)}
    >
      <option value="">All stages</option>
      {stageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.oppForm.stages.${s.code}`, s.label || s.code)}
  </option>
))}
    </select>
    {pipelineStageFilter && (
      <Button variant="outline" size="sm" onClick={() => setPipelineStageFilter("")}>
        Clear
      </Button>
    )}
  </div>
</div>
</div>


      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Left: Parties */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle>{t("crm.parties.title")}</CardTitle>
            <CardDescription>{t("crm.parties.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={(v)=>setTab(v||"All")}>
  <TabsList
    className="
      flex flex-wrap gap-2 justify-start
      w-full
    "
  >
    <TabsTrigger className="px-3 py-1.5 whitespace-nowrap" value="All">{t("crm.roles.all")}</TabsTrigger>
    <TabsTrigger className="px-3 py-1.5 whitespace-nowrap" value="Customer">{t("crm.roles.Customer")}</TabsTrigger>
    <TabsTrigger className="px-3 py-1.5 whitespace-nowrap" value="Supplier">{t("crm.roles.Supplier")}</TabsTrigger>
    <TabsTrigger className="px-3 py-1.5 whitespace-nowrap" value="Bank">{t("crm.roles.Bank")}</TabsTrigger>
    <TabsTrigger className="px-3 py-1.5 whitespace-nowrap" value="Partner">{t("crm.roles.Partner")}</TabsTrigger>
  </TabsList>
              <TabsContent value={tab} className="m-0">
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredParties.map(p => (
                    <div key={p.id} className="py-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
  <PartyItem p={p} onOpen={openParty} />
</div>
                      </div>
                    </div>
                  ))}
                  {loading && (
  <div className="py-10 text-center text-muted-foreground">{t("crm.parties.loading")}</div>
)}
{!loading && error && (
  <div className="py-10 text-center text-red-600">{error}</div>
)}
{!loading && !error && filteredParties.length === 0 && (
  <div className="py-10 text-center text-muted-foreground flex flex-col items-center gap-2">
    <CircleHelp className="h-6 w-6"/>
    <div>{t("crm.parties.noResults")}</div>
  </div>
)}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Right: Recent Activities */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>{t("crm.activities.title")}</CardTitle>
            <CardDescription>{t("crm.activities.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
  <div className="max-h-96 overflow-y-auto pr-1 divide-y">
    {recentActivities.map(a => (
      <ActivityRow key={a.id} a={a} onOpen={openActivityEditor} />
    ))}
    {recentActivities.length === 0 && (
      <div className="py-6 text-center text-muted-foreground text-sm">
        {t("crm.activities.none")}
      </div>
    )}
  </div>
<div className="mt-3">
  <Button
    type="button"                 // ⇐ bitno u formama
    variant="ghost"
    size="sm"
    className="gap-1 w-full"
    onClick={() => {
  setRecentActivitiesLimit(200);
  setAllActivitiesOpen(true);
}}
  >
    {t("crm.actions.showAll")} <ChevronRight className="h-4 w-4" />
  </Button>
</div>

</CardContent>
        </Card>
      </div>
      


      {/* Pipeline & Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle>{t("crm.pipeline.title")}</CardTitle>
            <CardDescription>{t("crm.pipeline.desc")}</CardDescription>
          </CardHeader>



          <CardContent>
            {/* Ovo je jednostavan preview; kasnije prebaciti u Kanban sa fazama */}
<div className="space-y-2 max-h-96 overflow-y-auto pr-1">
  {visibleOpps.map((o) => (
    <PipelineCard
  key={o.id}
  opp={o}
  stageOptions={stageOptions}
  t={t}
  onUpdateStage={async (stage) => {
    setOpps((prev) => prev.map((x) => (x.id === o.id ? { ...x, stage } : x)));
    try {
      await updateOpportunity(o.id, { stage });
    } catch (e) {
      const fresh = await fetchOpportunities();
      setOpps(fresh);
      alert("No saved: " + e.message);
    }
  }}
  onOpen={openOpportunityDetails}
/>
  ))}
  {visibleOpps.length === 0 && (
    <div className="text-sm text-muted-foreground py-4">
      Nema opportunity-ja za izabrani filter.
    </div>
  )}
</div>

          </CardContent>
        </Card>

        <Card>
  <CardHeader className="pb-2">
    <CardTitle>{t("crm.leads.title")}</CardTitle>
    <CardDescription>{t("crm.leads.desc")}</CardDescription>
  </CardHeader>
  <CardContent>
     {/* Toolbar */}
  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
  {sortedLeads.map((l) => (
    <div
      key={l.id}
      className="
        rounded-lg border p-3 transition
        bg-white dark:bg-neutral-900
        border-gray-200 dark:border-neutral-800
        hover:bg-slate-50 dark:hover:bg-neutral-900/60
      "
    >
      <div className="flex items-start gap-3">
        {/* LEFT */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="font-medium truncate">{l.partyName}</div>
            <span className="text-xs text-muted-foreground truncate">
              {l.source ?? "—"} • {l.stage}
            </span>

            {/* files count badge (ako imaš l.filesCount) */}
            {typeof l.filesCount === "number" && (
              <span
                title="Broj fajlova"
                className="
                  ml-1 inline-flex items-center rounded-full
                  border px-2 py-0.5 text-[11px] leading-none
                  border-gray-300 text-gray-700
                  dark:border-neutral-700 dark:text-gray-200
                "
              >
                {l.filesCount} {t("crm.attachments.filesCount")}{l.filesCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {/* quick note */}
          <div className="mt-2">
            <Input
  placeholder={t("crm.leads.quickNote")}
  className="h-8 w-full bg-white dark:bg-neutral-900 border border-gray-300 dark:border-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
  onKeyDown={async (e) => {
    if (e.key !== "Enter") return;
    const text = e.currentTarget.value.trim();
    if (!text) return;
    e.currentTarget.value = "";
    try {
      await addLeadNote(l.id, text);
      // opciono: lokalno uvećaj brojač ako ga imaš
      setLeads(prev =>
        prev.map(x => x.id === l.id ? { ...x, notesCount: (x.notesCount || 0) + 1 } : x)
      );
      // ako je otvoren modal za ovaj lead – refetch notes
      if (leadDetails?.id === l.id) {
        const notes = await fetchLeadNotes(l.id);
        setLeadNotes(Array.isArray(notes) ? notes : []);
      }
    } catch (err) {
      alert(err.message);
    }
  }}
/>
          </div>
        </div>

        {/* RIGHT (akcije) */}
        <div className="w-44 shrink-0 space-y-2">
          <select
            className="
              h-8 w-full rounded-md px-2 text-xs
              bg-white dark:bg-neutral-900
              border border-gray-300 dark:border-neutral-700
              focus:outline-none focus:ring-2 focus:ring-blue-500
            "
            value={l.stage || defaultLeadStage}
            onChange={async (e) => {
              const stage = e.target.value;
              // optimistički
              setLeads((prev) =>
                prev.map((x) => (x.id === l.id ? { ...x, stage } : x))
              );
              try {
                await updateLead(l.id, { status: stage });
              } catch (err) {
                alert("Nije sačuvano: " + err.message);
                const list = await fetchLeads({ q, stage: stageFilter, limit, offset: 0 });
                setLeads(list);
                setOffset(list.length);
                setHasMore(list.length === limit);
              }
            }}
          >
            {leadStageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.leadForm.stageValues.${s.code}`, s.label || s.code)}
  </option>
))}
          </select>

          <Input
            placeholder={t("crm.leads.assignTo")}
            className="
              h-8 w-full text-xs
              bg-white dark:bg-neutral-900
              border border-gray-300 dark:border-neutral-700
              focus:outline-none focus:ring-2 focus:ring-blue-500
            "
            defaultValue={l.assignedTo || ""}
            onBlur={async (e) => {
              const assignedTo = e.target.value.trim() || null;
              try {
                await updateLead(l.id, { assignedTo });
              } catch (err) {
                alert(err.message);
                e.target.value = l.assignedTo || "";
              }
            }}
          />

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setLeadDetails(l)}
              title="Prikaži fajlove i detalje"
            >
              {t("crm.pipeline.details")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={async () => {
                const name = prompt("Opportunity name", `Ponuda – ${l.partyName}`);
                const account = prompt("Account", l.partyName);
                if (!name || !account) return;
                try {
                  const res = await convertLead(l.id, { name, account });

if (!res?.success || !res?.token?.code) {
  alert("Track code nije generisan.");
  return;
}

window.open(`/offer/${res.token.code}`, "_blank");
                } catch (e) {
                  alert(e.message);
                }
              }}
            >
              Convert
            </Button>
          </div>
        </div>
      </div>
    </div>
  ))}

  {loading && (
    <div className="text-xs text-muted-foreground px-1 py-2">{t("crm.leads.loading")}</div>
  )}
</div>



<Separator className="my-3" />

<div className="flex gap-2">
  <Button
    variant="secondary"
    className="w-full"
    disabled={!hasMore || loading}
    onClick={loadMore}
  >
    {hasMore ? (loading ? t("crm.leads.loading") : t("crm.actions.loadMore")) : t("crm.actions.noMore")}
  </Button>
  <Button variant="outline" className="w-full gap-2" onClick={() => alert("TODO: Import leads")}>
    <PlusCircle className="h-4 w-4" /> {t("crm.actions.importLeads")}
  </Button>
</div>

   
  </CardContent>
</Card>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("crm.quickLinks.campaigns.title")}</CardTitle>
            <CardDescription>{t("crm.quickLinks.campaigns.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="gap-2 w-full" onClick={openCampaigns}>
  <Search className="h-4 w-4"/> {t("crm.quickLinks.campaigns.open")}
</Button>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("crm.quickLinks.reports.title")}</CardTitle>
            <CardDescription>{t("crm.quickLinks.reports.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="gap-2 w-full"><FileText className="h-4 w-4"/>{t("crm.quickLinks.reports.open")}</Button>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("crm.quickLinks.settings.title")}</CardTitle>
            <CardDescription>{t("crm.quickLinks.settings.desc")}</CardDescription>
            
          </CardHeader>
          <CardContent>
            <Button
  variant="outline"
  className="gap-2 w-full"
  onClick={() => navigate("/crm/settings")}
>
  <SettingsIcon />
  {t("crm.quickLinks.settings.open")}
</Button>
          </CardContent>
        </Card>
      </div>
      <Drawer
  open={drawerOpen}
  onClose={() => {
    setDrawerOpen(false);
    setSelected(null);
    setActivities([]);
    setContacts([]);
    setAddresses([]); setChannels([]); setBanks([]); setIdents([]);
  }}
  title={selected ? selected.displayName : t("crm.drawer.details")}
>
  {selected ? (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="flex flex-wrap gap-2">
        <TabsTrigger value="overview">{t("crm.drawer.details")}</TabsTrigger>
        <TabsTrigger value="contacts">{t("crm.drawer.contacts")}</TabsTrigger>
        <TabsTrigger value="addresses">{t("crm.addresses.title")}</TabsTrigger>
        <TabsTrigger value="channels">{t("crm.channels.title")}</TabsTrigger>
        <TabsTrigger value="bank">{t("crm.bank.title")}</TabsTrigger>
        <TabsTrigger value="identifiers">{t("crm.identifiers.title")}</TabsTrigger>
      </TabsList>

      {/* OVERVIEW (tvoj postojeći sadržaj) */}
      <TabsContent value="overview" className="space-y-4 pt-2">
        <div>
          <div className="text-sm text-gray-500 dark:text-neutral-400">{t("crm.drawer.roles")}</div>
          <div className="flex gap-2 mt-1">
            {(selected?.roleCodes ?? []).map(r => (
              <Badge key={r} variant="secondary">{r}</Badge>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 dark:text-neutral-400">{t("crm.drawer.location")}</div>
          <div className="mt-1">{selected.city || "—"} {selected.country ? `, ${selected.country}` : ""}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500 dark:text-neutral-400 mb-1">{t("crm.activities.title")}</div>
          <div className="divide-y">
  {activities.length ? activities.map(a => (
    <div key={a.id} className="py-2">
      <div className="font-medium">{a.subject}</div>
      <div className="text-xs text-gray-500 dark:text-neutral-400">
        {a.channel} • {a.when} • {a.status}
      </div>
      {a.notes ? (
        <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
          {a.notes}
        </div>
      ) : null}
    </div>
  )) : (
    <div className="text-sm text-gray-500">{t("crm.activities.none")}</div>
  )}
</div>

{activities.length > 0 && (
  <div className="mt-2 text-xs text-muted-foreground">
    Prikazano {activities.length} od {activitiesTotal}
  </div>
)}

{activitiesHasMore && (
  <div className="pt-2">
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={loadMoreActivities}
      disabled={activitiesLoadingMore}
    >
      {activitiesLoadingMore ? "Učitavanje..." : "Učitaj još"}
    </Button>
  </div>
)}
        </div>
        <div className="pt-2">
          <Button
  variant="outline"
  size="sm"
  onClick={() => {
    setActPartyLocked(true);

    setActForm({
      partyId: selected?.id ?? null,
      subject: "",
      channel: defaultActivityChannel,
      when: "",
      withParty: selected?.displayName || "",
      status: "open",
      notes: ""
    });

    setActOpen(true);
  }}
>
            {t("crm.drawer.addActivity")}
          </Button>
        </div>
      </TabsContent>

      {/* CONTACTS (tvoj postojeći kod) */}
      <TabsContent value="contacts" className="pt-2">
        <div className="text-sm text-gray-500 dark:text-neutral-400 mb-1">{t("crm.drawer.contacts")}</div>
        <div className="space-y-2">
          {contacts.map(c => (
            <div key={c.id} className="border rounded-md px-3 py-2">
              <div className="font-medium">{c.fullName}</div>
              <div className="text-xs text-muted-foreground">{c.title || "—"}</div>
              <div className="text-xs">{c.email || "—"} • {c.phone || "—"}</div>
            </div>
          ))}
          {contacts.length === 0 && (
            <div className="text-sm text-muted-foreground">{t("crm.drawer.noContacts")}</div>
          )}
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!selected?.id) { alert("Nije izabran party."); return; }
            if (!newC.fullName.trim()) return;

            try {
              await createContact({
                partyId: selected.id,
                fullName: newC.fullName,
                email: newC.email || null,
                phone: newC.phone || null,
                title: newC.title || null,
                notes: newC.notes || null,
              });
              const list = await fetchContactsForParty(selected.id);
              setContacts(Array.isArray(list) ? list : []);
              setNewC({ fullName: "", email: "", phone: "", title: "", notes: "" });
            } catch (err) {
              console.error(err);
              alert(err.message || "Greška pri snimanju kontakta.");
            }
          }}
          className="mt-3 grid gap-2"
        >
          <Input placeholder={`${t("crm.drawer.fullName")} *`} value={newC.fullName} onChange={e=>setNewC({...newC, fullName:e.target.value})}/>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder={t("crm.leadForm.email")} value={newC.email} onChange={e=>setNewC({...newC, email:e.target.value})}/>
            <Input placeholder={t("crm.leadForm.phone")} value={newC.phone} onChange={e=>setNewC({...newC, phone:e.target.value})}/>
          </div>
          <Input placeholder={t("crm.drawer.title")} value={newC.title} onChange={e=>setNewC({...newC, title:e.target.value})}/>
          <div className="flex justify-end">
            <Button type="submit" size="sm">{t("crm.drawer.addContact")}</Button>
          </div>
        </form>
      </TabsContent>

      {/* ADDRESSES */}
      <TabsContent value="addresses" className="pt-2">
        <AddressesPanel
          party={selected}
          items={addresses}
          refresh={async()=>setAddresses(await fetchAddressesForParty(selected.id))}
        />
      </TabsContent>

      {/* CHANNELS */}
      <TabsContent value="channels" className="pt-2">
        <ChannelsPanel
          party={selected}
          items={channels}
          refresh={async()=>setChannels(await fetchChannelsForParty(selected.id))}
        />
      </TabsContent>

      {/* BANK ACCOUNTS */}
      <TabsContent value="bank" className="pt-2">
        <BankPanel
          party={selected}
          items={banks}
          refresh={async()=>setBanks(await fetchBankAccountsForParty(selected.id))}
        />
      </TabsContent>

      {/* IDENTIFIERS */}
      <TabsContent value="identifiers" className="pt-2">
        <IdentifiersPanel
          party={selected}
          items={idents}
          refresh={async()=>setIdents(await fetchIdentifiersForParty(selected.id))}
        />
      </TabsContent>
    </Tabs>
  ) : null}
</Drawer>

<BigModal open={campaignsOpen} onClose={()=>{setCampaignsOpen(false); setSelectedCampaign(null);}} title="Campaigns">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {/* LEFT: campaigns list */}
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input placeholder="Search..." value={campQ} onChange={e=>setCampQ(e.target.value)} />
        <select className="h-9 rounded-md border px-2" value={campStatus} onChange={e=>setCampStatus(e.target.value)}>
          <option value="">All</option>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="ended">ended</option>
        </select>
        <Button type="button" onClick={openCampaigns}>Refresh</Button>
      </div>

      <Button
  type="button"
  onClick={async ()=>{
    const name = prompt("Campaign name?");
    if (!name) return;

    const landing_path = prompt("Landing path (npr /3d-scan)", "/3d-scan") || "/3d-scan";
    const utm_source = prompt("utm_source", "qr") || "qr";
    const utm_medium = prompt("utm_medium", "offline") || "offline";

    await createCampaign({
  name,
  landing_path,
  utm_source,
  utm_medium,
  status: "active",
  is_active: 1
});

    await openCampaigns();
  }}
>
  + Create campaign
</Button>

      <div className="max-h-[60vh] overflow-y-auto space-y-2">
        {campaigns.map(c=>(
          <button
            key={c.id}
            className={`w-full text-left border rounded-md p-3 hover:bg-slate-50 ${selectedCampaign?.id===c.id ? "ring-2 ring-blue-500" : ""}`}
            onClick={()=>openCampaign(c)}
          >
            <div className="font-medium">{c.name}</div>
            <div className="text-xs text-muted-foreground">{c.channel || "—"} • {c.status}</div>
          </button>
        ))}
      </div>
    </div>

    {/* RIGHT: tokens */}
    <div className="space-y-2">
      {!selectedCampaign ? (
        <div className="text-sm text-muted-foreground">Select campaign…</div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{selectedCampaign.name}</div>
              <div className="text-xs text-muted-foreground">{selectedCampaign.utm_source || ""}</div>
            </div>

            <Button
              type="button"
              onClick={async ()=>{
                const created = await createCampaignToken(selectedCampaign.id, { expires_days: 30 });
                const tk = await fetchCampaignTokens(selectedCampaign.id);
                setTokens(tk.items || tk);

                // napravi URL
                const url = `https://modularobjects.com/marketing/t/${created.code}`;
                await navigator.clipboard.writeText(url);
                alert("Token created + link copied");
              }}
            >
              Generate token
            </Button>
          </div>

          <div className="max-h-[55vh] overflow-y-auto space-y-2">
            {tokens.map(tk=>{
              const url = `https://modularobjects.com/marketing/t/${tk.code}`;
              return (
                <div key={tk.id} className="border rounded-md p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm">{tk.code}</div>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={()=>navigator.clipboard.writeText(url)}>
                        Copy link
                      </Button>
                      <Button type="button" size="sm" variant="destructive" onClick={async()=>{await revokeToken(tk.id); await openCampaign(selectedCampaign);}}>
                        Revoke
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="text-xs break-all text-muted-foreground">{url}</div>
                    <div className="flex md:justify-end">
                      <div className="bg-white p-2 rounded">
                        <QRCodeCanvas value={url} size={120} includeMargin />
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {tk.expires_at ? `Expires: ${new Date(tk.expires_at).toLocaleString()}` : "No expiry"} • {tk.revoked ? "revoked" : "active"}
                  </div>
                </div>
              );
            })}
            {tokens.length===0 && <div className="text-sm text-muted-foreground">No tokens yet.</div>}
          </div>
        </>
      )}
    </div>
  </div>
</BigModal>

<BigModal open={newOpen} onClose={() => setNewOpen(false)} title={t("crm.partyForm.title")}>
  <form
    onSubmit={async (e) => {
  e.preventDefault();
  if (!newForm.displayName.trim()) return;

  const created = await createParty({
  displayName: newForm.displayName,
  city: newForm.city,
  country: newForm.country,
  roleCodes: newForm.roleCodes   // 👈 dodaj ovo
});

  setNewOpen(false);
  setNewForm({ displayName: "", roleCodes: ["Customer"], city: "", country: "RS", tags: [] });

  const list = await fetchParties();
  setParties(list);

  // Otvori detalj i učitaj po ID
  setSelected(created);
setDrawerOpen(true);

// očisti kontakte dok ne učitaš sa servera
setContacts([]);
try {
  const acts = await fetchActivitiesForParty(created.id);
  setActivities(Array.isArray(acts) ? acts : []);
  const list = await fetchContactsForParty(created.id);
  setContacts(Array.isArray(list) ? list : []);
} catch {
  setActivities([]);
  setContacts([]);
}
}}
    className="space-y-3"
  >
    <div>
      <div className="text-sm mb-1">{t("crm.partyForm.name")}</div>
      <Input value={newForm.displayName} onChange={e => setNewForm({ ...newForm, displayName: e.target.value })} placeholder="npr. ACME d.o.o." />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.partyForm.city")}</div>
        <Input value={newForm.city} onChange={e => setNewForm({ ...newForm, city: e.target.value })} />
      </div>
      <div>
        <div className="text-sm mb-1">{t("crm.partyForm.country")}</div>
        <Input value={newForm.country} onChange={e => setNewForm({ ...newForm, country: e.target.value })} />
      </div>
    </div>
    <div className="flex gap-2">
      {["Customer","Supplier","Bank","Partner"].map(r => (
        <button
          type="button"
          key={r}
          className={`h-8 px-2 rounded border ${newForm.roleCodes.includes(r) ? "bg-blue-600 text-white border-blue-600" : ""}`}
          onClick={() => {
            const has = newForm.roleCodes.includes(r);
            setNewForm({ ...newForm, roleCodes: has ? newForm.roleCodes.filter(x=>x!==r) : [...newForm.roleCodes, r] });
          }}
        >
          {t(`crm.roles.${r}`, r)}
        </button>
      ))}
    </div>
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" type="button" onClick={() => setNewOpen(false)}>{t("crm.common.cancel")}</Button>
      <Button type="submit">{t("crm.common.save")}</Button>
    </div>
  </form>
</BigModal>
<BigModal open={actOpen} onClose={() => setActOpen(false)} title={t("crm.activityForm.title")}>
  <form
    onSubmit={async (e) => {
      e.preventDefault();

      if (!actForm.subject.trim()) return;

      if (!actForm.partyId) {
        alert("Izaberi party za aktivnost.");
        return;
      }

      await createActivityForParty(actForm.partyId, {
        subject: actForm.subject,
        channel: actForm.channel,
        when: actForm.when,
        status: actForm.status,
        notes: actForm.notes
      });

      console.log("NEW ACTIVITY submit", actForm);

      try {
  if (selected?.id === actForm.partyId) {
    const actsRes = await fetchActivitiesForParty(actForm.partyId, { limit: 50, offset: 0 });
    setActivities(actsRes.items);
    setActivitiesTotal(actsRes.total);
    setActivitiesOffset(actsRes.items.length);
    setActivitiesHasMore(actsRes.hasMore);
  }
} catch {}

      setActOpen(false);
      setActPartyLocked(false);
      setActForm({
        partyId: null,
        subject: "",
        channel: defaultActivityChannel,
        when: "",
        withParty: "",
        status: "open",
        notes: ""
      });
    }}
    className="space-y-3"
  >
    <div>
      <div className="text-sm mb-1">{t("crm.oppForm.account")}</div>

      {actPartyLocked ? (
        <Input
          value={actForm.withParty || ""}
          readOnly
          className="bg-gray-100 dark:bg-neutral-800"
        />
      ) : (
        <select
          className="
            h-9 w-full rounded-md px-3
            bg-white dark:bg-neutral-900
            border border-gray-400 dark:border-neutral-700
            focus:outline-none focus:ring-2 focus:ring-blue-500
          "
          value={actForm.partyId != null ? String(actForm.partyId) : ""}
          onChange={(e) => {
            const value = e.target.value;
            const partyId = value ? Number(value) : null;
            const party = parties.find((p) => String(p.id) === value);

            setActForm((prev) => ({
              ...prev,
              partyId,
              withParty: party?.displayName || ""
            }));
          }}
        >
          <option value="">Select party...</option>
          {parties.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.displayName}
            </option>
          ))}
        </select>
      )}

      {!actPartyLocked && (
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNewOpen(true)}
          >
            + New party
          </Button>
        </div>
      )}
    </div>

    <div>
      <div className="text-sm mb-1">{t("crm.activityForm.subject")}</div>
      <Input
        value={actForm.subject}
        onChange={(e) => setActForm((prev) => ({ ...prev, subject: e.target.value }))}
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.activityForm.channel")}</div>
        <select
          className="
            h-9 w-full rounded-md px-3
            bg-white dark:bg-neutral-900
            border border-gray-400 dark:border-neutral-700
            focus:outline-none focus:ring-2 focus:ring-blue-500
          "
          value={actForm.channel || defaultActivityChannel}
          onChange={(e) => setActForm((prev) => ({ ...prev, channel: e.target.value }))}
        >
          {channelOptions.map((c) => (
            <option key={c.code} value={c.code}>
              {t(`crm.activityForm.channelValues.${c.code}`, c.label || c.code)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="text-sm mb-1">{t("crm.activityForm.when")}</div>
        <input
          type="datetime-local"
          className="
            h-9 w-full rounded-md px-3
            bg-white dark:bg-neutral-900
            border border-gray-400 dark:border-neutral-700
            focus:outline-none focus:ring-2 focus:ring-blue-500
          "
          value={actForm.when}
          onChange={(e) => setActForm((prev) => ({ ...prev, when: e.target.value }))}
        />
      </div>
    </div>

    <div>
      <div className="text-sm mb-1">{t("crm.activityForm.status")}</div>
      <select
        className="
          h-9 w-full rounded-md px-3
          bg-white dark:bg-neutral-900
          border border-gray-400 dark:border-neutral-700
          focus:outline-none focus:ring-2 focus:ring-blue-500
        "
        value={actForm.status}
        onChange={(e) => setActForm((prev) => ({ ...prev, status: e.target.value }))}
      >
        <option value="open">{t("crm.activityForm.statusValues.open")}</option>
        <option value="done">{t("crm.activityForm.statusValues.done")}</option>
        <option value="canceled">{t("crm.activityForm.statusValues.canceled")}</option>
      </select>
    </div>

    <div>
      <div className="text-sm mb-1">{t("crm.activityForm.notes")}</div>
      <textarea
        rows={3}
        className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
        value={actForm.notes}
        onChange={(e) => setActForm((prev) => ({ ...prev, notes: e.target.value }))}
        placeholder="kratak opis..."
      />
    </div>

    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" type="button" onClick={() => setActOpen(false)}>
        {t("crm.common.cancel")}
      </Button>
      <Button type="submit">{t("crm.common.save")}</Button>
    </div>
  </form>
</BigModal>
<BigModal open={leadOpen} onClose={() => setLeadOpen(false)} title={t("crm.leadForm.title")}>
  <form
    onSubmit={async (e) => {
  e.preventDefault();

  if (!leadForm.partyId && !leadForm.partyName.trim()) {
    alert("Izaberi party ili dodaj novi.");
    return;
  }

  await createLead({
    ...leadForm,
    score: Number(leadForm.score || 0)
  });

  const list = await fetchLeads();
  setLeads(list);

  setLeadOpen(false);
  setLeadForm({
    partyId: null,
    partyName: "",
    contactName: "",
    phone: "",
    email: "",
    source: "Manual",
    score: 0,
    stage: defaultLeadStage,
    notes: ""
  });
}}
    className="space-y-3"
  >
    <div>
  <div className="text-sm mb-1">{t("crm.leadForm.party")}</div>

  <select
    className="
      h-9 w-full rounded-md px-3
      bg-white dark:bg-neutral-900
      border border-gray-400 dark:border-neutral-700
      focus:outline-none focus:ring-2 focus:ring-blue-500
    "
    value={leadForm.partyId != null ? String(leadForm.partyId) : ""}
    onChange={(e) => {
      const value = e.target.value;
      const partyId = value ? Number(value) : null;
      const party = parties.find((p) => String(p.id) === value);

      setLeadForm((prev) => ({
        ...prev,
        partyId,
        partyName: party?.displayName || "",
        contactName: prev.contactName || party?.contactName || "",
        phone: prev.phone || party?.phone || "",
        email: prev.email || party?.email || ""
      }));
    }}
  >
    <option value="">{t("crm.leadForm.selectParty", "Select party...")}</option>
    {parties.map((p) => (
      <option key={p.id} value={String(p.id)}>
        {p.displayName}
      </option>
    ))}
  </select>

  <div className="mt-2 flex justify-end">
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setNewOpen(true)}
    >
      + New party
    </Button>
  </div>
</div>

    <div>
  <div className="text-sm mb-1">{t("crm.leadForm.contactName")}</div>
  <Input
    value={leadForm.contactName}
    onChange={e => setLeadForm({ ...leadForm, contactName: e.target.value })}
    placeholder={t("crm.leadForm.placeholderContact")}
  />
</div>

<div>
  <div className="text-sm mb-1">{t("crm.leadForm.phone")}</div>
  <Input
    type="tel"
    value={leadForm.phone}
    onChange={e => setLeadForm({ ...leadForm, phone: e.target.value })}
    placeholder={t("crm.leadForm.placeholderPhone")}
  />
</div>

<div>
  <div className="text-sm mb-1">{t("crm.leadForm.email")}</div>
  <Input
    type="email"
    value={leadForm.email}
    onChange={e => setLeadForm({ ...leadForm, email: e.target.value })}
    placeholder={t("crm.leadForm.placeholderEmail")}
  />
</div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.leadForm.source")}</div>
        <select
          className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

          value={leadForm.source}
          onChange={e => setLeadForm({ ...leadForm, source: e.target.value })}
        >
          <option value="Manual">{t("crm.leadForm.sourceValues.Manual")}</option>
          <option value="Web">{t("crm.leadForm.sourceValues.Web")}</option>
          <option value="QR">{t("crm.leadForm.sourceValues.QR")}</option>
          <option value="Referral">{t("crm.leadForm.sourceValues.Referral")}</option>
        </select>
      </div>
      <div>
        <div className="text-sm mb-1">{t("crm.leadForm.score")}</div>
        <Input
          type="number"
          value={leadForm.score}
          onChange={e => setLeadForm({ ...leadForm, score: Number(e.target.value) })}
        />
      </div>
    </div>

    <div>
      <div className="text-sm mb-1">{t("crm.leadForm.stage")}</div>
      <select
        className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

        value={leadForm.stage}
        onChange={e => setLeadForm({ ...leadForm, stage: e.target.value })}
      >
        {leadStageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.leadForm.stageValues.${s.code}`, s.label || s.code)}
  </option>
))}
      </select>
    </div>

    <div>
      <div className="text-sm mb-1">{t("crm.leadForm.notes")}</div>
      <textarea
        rows={3}
        className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
        value={leadForm.notes}
        onChange={e => setLeadForm({ ...leadForm, notes: e.target.value })}
        placeholder="kratak opis..."
      />
    </div>

    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" type="button" onClick={() => setLeadOpen(false)}>{t("crm.common.cancel")}</Button>
      <Button type="submit">{t("crm.common.save")}</Button>
    </div>
  </form>
</BigModal>
<BigModal open={oppOpen} onClose={() => setOppOpen(false)} title={t("crm.oppForm.title")}>
  <form
    onSubmit={async (e) => {
  e.preventDefault();

  console.log("oppForm before create:", oppForm);

  if (!oppForm.partyId) {
  alert("Izaberi party u polju Account (Party).");
  return;
}

  try {
    await createOpportunity({
      ...oppForm,
      amount: Number(oppForm.amount || 0)
    });
    const list = await fetchOpportunities();
    setOpps(list);
    setOppOpen(false);
    setOppForm({
  name: "",
  account: "",
  partyId: null,
  amount: "",
  currency: "EUR",
  stage: defaultOpportunityStage,
  closeDate: "",
  notes: ""
});
  } catch (err) {
    alert(err.message || "Greška pri kreiranju opportunity-ja");
  }
}}
    className="space-y-3"
  >
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.name")}</div>
        <Input
          value={oppForm.name}
          onChange={e => setOppForm({ ...oppForm, name: e.target.value })}
          placeholder="exm. Kitchen – 6 pcs"
        />
      </div>
      <div>
  <div className="text-sm mb-1">{t("crm.oppForm.account")}</div>
  <select
  className="
    h-9 w-full rounded-md px-3
    bg-white dark:bg-neutral-900
    border border-gray-400 dark:border-neutral-700
    focus:outline-none focus:ring-2 focus:ring-blue-500
  "
  value={oppForm.partyId != null ? String(oppForm.partyId) : ""}
  onChange={(e) => {
    const value = e.target.value;
    const partyId = value ? Number(value) : null;
    const party = parties.find((p) => String(p.id) === value);

    console.log("SELECT CHANGE", {
      value,
      partyId,
      party,
    });

    setOppForm((prev) => ({
      ...prev,
      partyId,
      account: party?.displayName || "",
    }));
  }}
>
  <option value="">Select party...</option>
  {parties.map((p) => (
    <option key={p.id} value={String(p.id)}>
      {p.displayName}
    </option>
  ))}
</select>

  <div className="mt-2 flex justify-end">
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setNewOpen(true)}
    >
      + New party
    </Button>
  </div>
</div>
    </div>

    <div className="grid grid-cols-3 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.amount")}</div>
        <Input
          type="number"
          value={oppForm.amount}
          onChange={e => setOppForm({ ...oppForm, amount: e.target.value })}
        />
      </div>
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.currency")}</div>
        <select
          className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

          value={oppForm.currency}
          onChange={e => setOppForm({ ...oppForm, currency: e.target.value })}
        >
          <option>EUR</option>
          <option>USD</option>
          <option>RSD</option>
        </select>
      </div>
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.stage")}</div>
        <select
          className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

          value={oppForm.stage}
          onChange={e => setOppForm({ ...oppForm, stage: e.target.value })}
        >
          {stageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.oppForm.stages.${s.code}`, s.label || s.code)}
  </option>
))}
        </select>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.closeDate")}</div>
        <input
          type="date"
          className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

          value={oppForm.closeDate}
          onChange={e => setOppForm({ ...oppForm, closeDate: e.target.value })}
        />
      </div>
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.notes")}</div>
        <Input
          value={oppForm.notes}
          onChange={e => setOppForm({ ...oppForm, notes: e.target.value })}
          placeholder="kratak opis..."
        />
      </div>
    </div>

    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" type="button" onClick={() => setOppOpen(false)}>{t("crm.common.cancel")}</Button>
      <Button type="submit">{t("crm.common.save")}</Button>
    </div>
  </form>
</BigModal>
<BigModal open={!!leadDetails} onClose={() => setLeadDetails(null)} title="Lead detalji">
  {leadDetails && (
    <div className="space-y-3 text-sm">

      {/* GORNJI BLOK: osnovni podaci (levo) + QR (desno) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Osnovni podaci */}
        <div className="space-y-2">
          <div><b>{t("crm.drawer.fullName")}:</b> {leadDetails.partyName}</div>
          <div><b>{t("crm.leadForm.phone")}:</b> {leadDetails.phone || "—"}</div>
          <div><b>{t("crm.leadForm.email")}:</b> {leadDetails.email || "—"}</div>
          <div><b>{t("crm.leadForm.source")}:</b> {leadDetails.source || "—"}</div>
          <div><b>{t("crm.leadForm.stage")}:</b> {leadDetails.stage}</div>
        </div>

        {/* QR blok */}
        <div className="flex md:justify-end">
          <div className="border rounded-lg p-3 w-[220px] text-center shadow-sm">
            <div className="font-medium mb-2">QR kontakt</div>
            {(() => {
  const fullName = leadDetails.partyName || "Kontakt";
  const phone = (leadDetails.phone || "").replace(/\s+/g, "");
  const email = leadDetails.email || "";
  const org = "Modular Objects Customer";

  // razdvajanje imena i prezimena
  const [lastName, firstName] = (() => {
    const parts = fullName.trim().split(" ");
    const first = parts.slice(0, -1).join(" ") || "";  // sve osim poslednje reči
    const last = parts.slice(-1).join("") || "";       // poslednja reč
    return [last, first];
  })();

  const vcard = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${lastName};${firstName};;;`,
    `FN:${fullName}`,
    org ? `ORG:${org}` : "",
    phone ? `TEL;TYPE=CELL:${phone}` : "",
    email ? `EMAIL;TYPE=INTERNET:${email}` : "",
    "END:VCARD"
  ].filter(Boolean).join("\n");

  const qrValue = vcard;

  return (
    <>
      <div className="mx-auto inline-block bg-white rounded-md">
        <QRCodeCanvas value={qrValue} size={160} includeMargin />
      </div>
      <div className="mt-2 text-[11px] text-slate-600">
        Skeniraj da dodaš kontakt u telefon.
      </div>
      <a
        className="mt-2 inline-block text-xs underline text-blue-600"
        href={`data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`}
        download={`${fullName}.vcf`}
      >
        Preuzmi vCard (.vcf)
      </a>
    </>
  );
})()}

          </div>
        </div>
      </div>

      {/* NOTES PREKO CELE ŠIRINE */}
      <div className="mt-1">
        <b>{t("crm.leadForm.notes")}:</b>
        <div className="whitespace-pre-wrap mt-1 leading-relaxed">
          {leadDetails.message}
        </div>
      </div>

      {/* Datum kreiranja */}
      <div className="text-xs text-muted-foreground">
        <b>{t("crm.common.created")}:</b>{" "}
        {leadDetails.createdAt ? new Date(leadDetails.createdAt).toLocaleString() : "—"}
      </div>

      {/* PRILOZI */}
      <div className="mt-2">
        <div className="text-sm font-medium mb-2">{t("crm.attachments.title")}</div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
          {leadFiles.map(f => (
            <div key={f.id} className="border rounded p-2">
              {f.mimeType?.startsWith("image/") ? (
                <a href={f.url} target="_blank" rel="noreferrer">
                  <img src={f.url} alt={f.fileName} className="h-24 w-full object-cover rounded" />
                </a>
              ) : (
                <a href={f.url} target="_blank" rel="noreferrer" className="text-xs underline break-all">
                  {f.fileName}
                </a>
              )}
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{formatKB(f.fileSize)}</span>
                <button
                  className="text-[11px] text-red-600"
                  onClick={async () => {
                    try {
                      await deleteLeadFile(leadDetails.id, f.id);
                      setLeadFiles(prev => prev.filter(x => x.id !== f.id));
                    } catch (e) { alert(e.message); }
                  }}
                >
                  {t("crm.common.delete")}
                </button>
              </div>
            </div>
          ))}
          {leadFiles.length === 0 && (
            <div className="text-xs text-slate-500">{t("crm.attachments.none")}</div>
          )}
        </div>

        {/* upload novih fajlova */}
        <div className="mt-3 flex items-center gap-2">
          <input
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              const bad = files.find(f => f.size > 10 * 1024 * 1024);
              if (bad) { alert("Maksimalna veličina fajla je 10MB."); return; }
              setNewFiles(files);
            }}
          />
        </div>
      </div>

      {/* Dugme Upload */}
      <div className="flex justify-end pt-2">
        <Button
          disabled={!newFiles.length || uploading}
          onClick={async () => {
            if (!leadDetails?.id || !newFiles.length) return;
            try {
              setUploading(true);
              await uploadLeadFiles(leadDetails.id, newFiles);
              const fresh = await fetchLeadFiles(leadDetails.id);
              setLeadFiles(fresh);
              setNewFiles([]);
            } catch (e) {
              alert(e.message || "Greška pri uploadu.");
            } finally {
              setUploading(false);
            }
          }}
        >
          {uploading ? t("crm.common.uploading") : t("crm.common.upload")}
        </Button>
      </div>
    </div>
  )}
</BigModal>

<BigModal
  open={!!activityDetails}
  onClose={closeActivity}
  title={activityDetails?.subject || t("crm.activities.title")}
>
  {activityDetails ? (
    <div className="space-y-2 text-sm">
      <div className="text-xs text-muted-foreground">
        {activityDetails.withParty} • {activityDetails.when}
      </div>
      <div>
        <b>Channel:</b> {activityDetails.channel || "—"}
      </div>
      <div>
        <b>Status:</b> {activityDetails.status || "—"}
      </div>
      {activityDetails.notes && (
        <div className="whitespace-pre-wrap">{activityDetails.notes}</div>
      )}
      <div className="flex justify-end pt-2">
        <Button type="button" onClick={closeActivity}>{t("crm.common.close") || "Close"}</Button>
      </div>
    </div>
  ) : null}
</BigModal>

<BigModal
  open={allActivitiesOpen}
  onClose={() => setAllActivitiesOpen(false)}
  title={t("crm.activities.title")}
>
  <div className="max-h-[70vh] overflow-y-auto divide-y">
    {recentActivities.map(a => (
      <ActivityRow
        key={`all-${a.id}`}
        a={a}
        onOpen={(x) => { setAllActivitiesOpen(false); openActivity(x); }}
      />
    ))}
    {recentActivities.length === 0 && (
      <div className="py-6 text-center text-muted-foreground text-sm">
        {t("crm.activities.none")}
      </div>
    )}
  </div>
  <div className="flex justify-end pt-3">
    <Button type="button" variant="outline" onClick={() => setAllActivitiesOpen(false)}>
      {t("crm.common.close") || "Close"}
    </Button>
  </div>
</BigModal>

<BigModal
  open={!!activityEdit}
  onClose={() => setActivityEdit(null)}
  title={t("crm.activityForm.titleEdit") || "Edit Activity"}
>
  {activityEdit && (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setActivitySaving(true);

        try {
          await updateActivity(activityEdit.id, {
            partyId: activityEdit.partyId || null,
            subject: activityEdit.subject || null,
            channel: activityEdit.channel || null,
            when: activityEdit.when || null,
            status: activityEdit.status || null,
            notes: activityEdit.notes || null
          });

          setRecentActivities((prev) =>
            prev.map((x) =>
              x.id === activityEdit.id
                ? {
                    ...x,
                    partyId: activityEdit.partyId,
                    withParty: activityEdit.withParty,
                    subject: activityEdit.subject,
                    channel: activityEdit.channel,
                    when: (activityEdit.when || "").replace("T", " "),
                    status: activityEdit.status,
                    notes: activityEdit.notes
                  }
                : x
            )
          );

          if (selected?.id && selected.id === activityEdit.partyId) {
            try {
              setActivities(await fetchActivitiesForParty(selected.id));
            } catch {}
          }

          setActivityEdit(null);
        } catch (err) {
          alert(err.message || "Greška pri čuvanju aktivnosti.");
        } finally {
          setActivitySaving(false);
        }
      }}
    >
      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.account")}</div>
        <select
          className="
            h-9 w-full rounded-md px-3
            bg-white dark:bg-neutral-900
            border border-gray-400 dark:border-neutral-700
            focus:outline-none focus:ring-2 focus:ring-blue-500
          "
          value={activityEdit.partyId != null ? String(activityEdit.partyId) : ""}
          onChange={(e) => {
            const value = e.target.value;
            const partyId = value ? Number(value) : null;
            const party = parties.find((p) => String(p.id) === value);

            setActivityEdit((prev) => ({
              ...prev,
              partyId,
              withParty: party?.displayName || ""
            }));
          }}
        >
          <option value="">Select party...</option>
          {parties.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.displayName}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="text-sm mb-1">{t("crm.activityForm.subject")}</div>
        <Input
          value={activityEdit.subject}
          onChange={(e) => setActivityEdit((s) => ({ ...s, subject: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm mb-1">{t("crm.activityForm.channel")}</div>
          <select
            className="h-9 w-full rounded-md px-3 bg-white dark:bg-neutral-900 border border-gray-400 dark:border-neutral-700"
            value={activityEdit.channel || defaultActivityChannel}
            onChange={(e) => setActivityEdit((s) => ({ ...s, channel: e.target.value }))}
          >
            {channelOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {t(`crm.activityForm.channelValues.${c.code}`, c.label || c.code)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-sm mb-1">{t("crm.activityForm.when")}</div>
          <input
            type="datetime-local"
            className="h-9 w-full rounded-md px-3 bg-white dark:bg-neutral-900 border border-gray-400 dark:border-neutral-700"
            value={activityEdit.when}
            onChange={(e) => setActivityEdit((s) => ({ ...s, when: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <div className="text-sm mb-1">{t("crm.activityForm.status")}</div>
        <select
          className="h-9 w-full rounded-md px-3 bg-white dark:bg-neutral-900 border border-gray-400 dark:border-neutral-700"
          value={activityEdit.status}
          onChange={(e) => setActivityEdit((s) => ({ ...s, status: e.target.value }))}
        >
          <option value="open">{t("crm.activityForm.statusValues.open")}</option>
          <option value="done">{t("crm.activityForm.statusValues.done")}</option>
          <option value="canceled">{t("crm.activityForm.statusValues.canceled")}</option>
        </select>
      </div>

      <div>
        <div className="text-sm mb-1">{t("crm.activityForm.notes")}</div>
        <textarea
          rows={3}
          className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
          value={activityEdit.notes}
          onChange={(e) => setActivityEdit((s) => ({ ...s, notes: e.target.value }))}
          placeholder="kratak opis..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => setActivityEdit(null)}>
          {t("crm.common.cancel")}
        </Button>
        <Button type="submit" disabled={activitySaving}>
          {activitySaving ? t("crm.common.saving") || "Saving..." : t("crm.common.save")}
        </Button>
      </div>
    </form>
  )}
</BigModal>


<BigModal open={oppDetailsOpen} onClose={() => setOppDetailsOpen(false)} title={t("crm.opportunityDetails.title")}>
  {oppDetails ? (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await updateOpportunity(oppDetails.id, {
            name: oppDetails.name,
            account: oppDetails.account,
            amount: oppDetails.amount === "" ? null : Number(oppDetails.amount),
            currency: oppDetails.currency,
            stage: oppDetails.stage,
            closeDate: oppDetails.closeDate || null,
            notes: oppDetails.notes || null,
          });

          
          // refresh liste (opciono)
          const list = await fetchOpportunities();
          setOpps(list);
          setOppDetailsOpen(false);
        } catch (err) {
          alert("Greška pri čuvanju: " + err.message);
        }
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.name")}</div>
          <Input
            value={oppDetails.name || ""}
            onChange={(e) => setOppDetails(d => ({ ...d, name: e.target.value }))}
          />
        </div>
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.account")}</div>
          <Input
            value={oppDetails.account || ""}
            onChange={(e) => setOppDetails(d => ({ ...d, account: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.amount")}</div>
          <Input
            type="number"
            value={oppDetails.amount ?? ""}
            onChange={(e) => setOppDetails(d => ({ ...d, amount: e.target.value }))}
          />
        </div>
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.currency")}</div>
          <select
            className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

            value={oppDetails.currency || "EUR"}
            onChange={(e) => setOppDetails(d => ({ ...d, currency: e.target.value }))}
          >
            <option>EUR</option>
            <option>USD</option>
            <option>RSD</option>
          </select>
        </div>
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.stage")}</div>
          <select
            className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

            value={oppDetails.stage || "qualification"}
            onChange={(e) => setOppDetails(d => ({ ...d, stage: e.target.value }))}
          >
            {stageOptions.map((s) => (
  <option key={s.code} value={s.code}>
    {t(`crm.oppForm.stages.${s.code}`, s.label || s.code)}
  </option>
))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm mb-1">{t("crm.oppForm.closeDate")}</div>
          <input
            type="date"
            className="
  h-9 w-full rounded-md px-3
  bg-white dark:bg-neutral-900
  border border-gray-400 dark:border-neutral-700
  focus:outline-none focus:ring-2 focus:ring-blue-500
"

            value={oppDetails.closeDate || ""}
            onChange={(e) => setOppDetails(d => ({ ...d, closeDate: e.target.value }))}
          />
        </div>
        <div className="text-xs text-muted-foreground flex items-end">
          {t("crm.common.created")} {oppDetails.createdAt ? new Date(oppDetails.createdAt).toLocaleString() : "—"}
        </div>
      </div>

      <div>
        <div className="text-sm mb-1">{t("crm.oppForm.notes")}</div>
        <textarea
          rows={3}
          className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
          value={oppDetails.notes || ""}
          onChange={(e) => setOppDetails(d => ({ ...d, notes: e.target.value }))}
          placeholder="kratak opis / status…"
        />
      </div>

<div className="border-t pt-3 mt-3">
  <div className="text-sm font-medium mb-1">{t("crm.opportunityDetails.activity")}</div>
  
  <div className="flex gap-2">
    <Input
      placeholder="Dodaj belešku… (Enter)"
      onKeyDown={async (e)=>{
        if(e.key!=="Enter"||!e.currentTarget.value.trim()) return;
        const t = e.currentTarget.value; e.currentTarget.value="";
        try { await addOpportunityNote(oppDetails.id, t); const {items} = await fetchOpportunityNotes(oppDetails.id); setOppDetails(d=>({...d, notesList: items})); }
        catch(err){ alert(err.message); }
      }}
    />
  </div>
  <div className="mt-2 max-h-40 overflow-y-auto space-y-2 text-sm">
    {(oppDetails.notesList||[]).map(n=>(
      <div key={n.Id} className="border rounded-md px-2 py-1">
        <div>{n.Text}</div>
        <div className="text-xs text-muted-foreground">{new Date(n.CreatedAtUtc).toLocaleString()}</div>
      </div>
    ))}
  </div>
</div>

      {oppDetails.leadName && (
        <div className="text-xs text-muted-foreground">
          {t("crm.opportunityDetails.linkedLead")} {oppDetails.leadName}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">

{oppDetails?.linkedProjectId && (
  <div className="rounded-md border px-3 py-2 text-sm bg-slate-50 dark:bg-neutral-900">
    <div className="font-medium">
      Linked project: {oppDetails.linkedProjectName || `#${oppDetails.linkedProjectId}`}
    </div>
    <div className="text-xs text-muted-foreground">
      ID: {oppDetails.linkedProjectId}
      {oppDetails.linkedProjectStatus ? ` • Status: ${oppDetails.linkedProjectStatus}` : ""}
    </div>
  </div>
)}

<Button
  type="button"
  onClick={async () => {
    try {
      if (oppDetails?.linkedProjectId) {
        setOppDetailsOpen(false);
        navigate(`/project/${oppDetails.linkedProjectId}/overview`);
        return;
      }

      const result = await createProjectFromOpportunity(oppDetails.id);
      const projectId =
        result?.item?.id ||
        result?.item?.projectId ||
        result?.projectId;

      if (!projectId) {
        throw new Error("Project ID was not returned");
      }

      setOppDetails((prev) => ({
        ...prev,
        linkedProjectId: projectId
      }));

      setOppDetailsOpen(false);
      navigate(`/project/${projectId}/overview`);
    } catch (e) {
      alert("Greška pri kreiranju/otvaranju projekta: " + e.message);
    }
  }}
>
  {oppDetails?.linkedProjectId
    ? t("crm.opportunityDetails.openProject", "Open Project")
    : t("crm.opportunityDetails.createProject", "Create Project")}
</Button>

        <Button variant="outline" type="button" onClick={() => setOppDetailsOpen(false)}>{t("crm.common.cancel")}</Button>
        <Button
  type="button"
  variant="destructive"
  onClick={async ()=>{
    if (!confirm("Obrisati opportunity?")) return;
    try { await deleteOpportunity(oppDetails.id); setOppDetailsOpen(false); setOpps(await fetchOpportunities()); }
    catch(e){ alert(e.message); }
  }}
>
  {t("crm.common.delete")}
</Button>
        <Button type="submit">{t("crm.common.save")}</Button>
      </div>
    </form>
  ) : (
    <div className="text-sm text-muted-foreground">{t("crm.parties.loading")}</div>
  )}
</BigModal>

<BigModal open={infoOpen} onClose={() => setInfoOpen(false)} title="CRM Guide / System Specification">
  <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
    {/* LEFT NAV */}
    <div className="border rounded-lg p-2 h-fit">
      <div className="space-y-1">
        {[
          ["overview", "Overview"],
          ["entities", "Entities"],
          ["workflow", "Workflow"],
          ["activities", "Activities"],
          ["pipeline", "Pipeline"],
          ["campaigns", "Campaigns"],
          ["implemented", "Implemented"],
          ["roadmap", "Roadmap"],
          ["rules", "Rules & Logic"],
          ["vision", "Future Vision"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setInfoTab(key)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
              infoTab === key
                ? "bg-blue-600 text-white"
                : "hover:bg-slate-100 dark:hover:bg-neutral-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {/* CONTENT */}
    <div className="max-h-[75vh] overflow-y-auto pr-2 space-y-6">
      {infoTab === "overview" && (
        <>
          <GuideSection icon={Info} title="1. Purpose of the CRM">
            <p>
              CRM modul u ModularObjects platformi služi za upravljanje odnosima sa klijentima,
              prodajnim prilikama, aktivnostima i internim procesima koji vode od prvog kontakta
              do ponude i dalje do projekta.
            </p>
            <p>
              Osnovna svrha sistema je da objedini marketing, prodaju, organizaciju zadataka i
              pripremu poslovnih procesa u jednom mestu, tako da svi učesnici imaju pregled šta je
              urađeno, šta je trenutno aktivno i šta je sledeći korak.
            </p>
          </GuideSection>

          <GuideSection icon={Workflow} title="2. Main Process Flow">
            <div className="space-y-2">
              <div className="font-medium">Lead → Party → Opportunity → Activities → Offer → Project</div>
              <p>
                Lead predstavlja prvi kontakt ili upit. Party predstavlja fizičko ili pravno lice
                sa kojim radimo. Opportunity predstavlja konkretnu prodajnu priliku. Activities
                predstavljaju sve zadatke i komunikaciju. Nakon toga sledi ponuda, a kasnije i
                realizacija projekta.
              </p>
            </div>
          </GuideSection>

          <GuideSection icon={Target} title="3. Core Principle">
            <p>
              CRM mora da bude centralno mesto za rad prodaje i operacije. Svaki zapis mora imati
              jasan kontekst: kome pripada, u kom je statusu, ko je odgovoran i koji su naredni koraci.
            </p>
          </GuideSection>
        </>
      )}

      {infoTab === "entities" && (
        <>
          <GuideSection icon={FileText} title="Lead">
            <p>
              Lead je početni zapis koji nastaje iz web forme, ručnog unosa, preporuke, kampanje
              ili drugog izvora. Lead služi za evidenciju početnog interesovanja.
            </p>
            <div className="mt-2">
              <b>Polja:</b> Name, Phone, Email, SourceCode, Status, Message, Notes, AssignedTo, CreatedAt
            </div>
          </GuideSection>

          <GuideSection icon={Building2} title="Party">
            <p>
              Party je centralni poslovni entitet i može predstavljati klijenta, partnera,
              dobavljača, banku ili drugi tip subjekta.
            </p>
            <div className="mt-2">
              <b>Polja:</b> DisplayName, City, Country, Roles, CreatedAt
            </div>
          </GuideSection>

          <GuideSection icon={Users} title="Contacts">
            <p>
              Kontakt osobe vezane za party. Jedna firma može imati više kontakata.
            </p>
            <div className="mt-2">
              <b>Polja:</b> FullName, Email, Phone, Title, Notes
            </div>
          </GuideSection>

          <GuideSection icon={MapPin} title="Addresses">
            <p>
              Adrese povezane sa party zapisom. Mogu predstavljati sedište, gradilište, magacin,
              billing adresu i slično.
            </p>
          </GuideSection>

          <GuideSection icon={Mail} title="Channels">
            <p>
              Kanali komunikacije vezani za party: email, telefon, website, društvene mreže i drugi kontakti.
            </p>
          </GuideSection>

          <GuideSection icon={Banknote} title="Bank Accounts / Identifiers">
            <p>
              Bankovni računi i identifikatori služe za poslovni i administrativni kontekst partnera.
              Ovi podaci kasnije mogu biti korisni za povezivanje sa ERP i finansijskim tokovima.
            </p>
          </GuideSection>

          <GuideSection icon={Briefcase} title="Opportunity">
            <p>
              Opportunity predstavlja prodajnu priliku. Uvek treba da bude povezan sa Party zapisom.
            </p>
            <div className="mt-2">
              <b>Polja:</b> Name, Account, PartyId, Amount, Currency, Stage, CloseDate, Notes, CreatedAt
            </div>
          </GuideSection>

          <GuideSection icon={ActivityIcon} title="Activity">
            <p>
              Activity predstavlja zadatak, komunikaciju ili operativnu akciju vezanu za party ili opportunity.
            </p>
            <div className="mt-2">
              <b>Polja:</b> Subject, Channel, WhenAt, Status, Notes, PartyId
            </div>
          </GuideSection>
        </>
      )}

      {infoTab === "workflow" && (
        <>
          <GuideSection icon={Workflow} title="Workflow Logic">
            <p>
              Dugoročni cilj je da workflow bude automatizovan. Kada se kreira opportunity ili kada
              se promeni stage, sistem treba da generiše odgovarajuće aktivnosti na osnovu template-a.
            </p>
          </GuideSection>

          <GuideSection icon={Layers3} title="Expected Workflow Engine">
            <p>
              Workflow engine treba da koristi stage opportunity-ja kao ulaz i da na osnovu
              definisanih template-a kreira zadatke za odgovorne role.
            </p>
            <div className="mt-2">
              Primer:
            </div>
            <div className="mt-1 pl-4">
              Qualification → Sales follow-up  
              Proposal → Prepare design / cost estimate  
              Negotiation → Final review / commercial alignment  
              Won → Project handover / internal kickoff
            </div>
          </GuideSection>

          <GuideSection icon={ClipboardList} title="Current State">
            <div className="flex items-center gap-2 mb-2">
              <StatusBadge status="progress" />
            </div>
            <p>
              Backend logika za automatsko generisanje aktivnosti još nije kompletno povezana sa
              create/update opportunity rutama. Ručni unos aktivnosti radi, ali workflow automatizacija
              još nije završena.
            </p>
          </GuideSection>
        </>
      )}

      {infoTab === "activities" && (
        <>
          <GuideSection icon={ActivityIcon} title="How Activities Work">
            <p>
              Activity zapis služi za evidentiranje svega što treba uraditi ili je već urađeno u odnosu
              na party i opportunity.
            </p>
          </GuideSection>

          <GuideSection icon={CirclePlus} title="Manual Activity Creation">
            <p>
              Postoje dva validna načina za ručno kreiranje aktivnosti:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                <b>Global New Activity:</b> korisnik mora ručno da izabere party.
              </li>
              <li>
                <b>Activity from Party drawer:</b> party je već poznat i mora ostati zaključan.
              </li>
            </ul>
          </GuideSection>

          <GuideSection icon={CheckCircle2} title="Statuses">
            <p>
              Activity status trenutno podržava:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>open</li>
              <li>done</li>
              <li>canceled</li>
            </ul>
          </GuideSection>

          <GuideSection icon={Clock3} title="Display and Loading">
            <p>
              Recent Activities kartica prikazuje najnovije aktivnosti u sistemu. Za veće količine
              podataka treba uvesti postepeno učitavanje, paginaciju ili load more logiku.
            </p>
          </GuideSection>
        </>
      )}

      {infoTab === "pipeline" && (
        <>
          <GuideSection icon={FolderKanban} title="Opportunity Pipeline">
            <p>
              Pipeline prikazuje opportunity-je grupisane i filtrirane po stage statusu. Korisnik može:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>pregledati opportunity zapise</li>
              <li>menjati stage</li>
              <li>otvoriti detalje</li>
              <li>videti amount, currency i close date</li>
            </ul>
          </GuideSection>

          <GuideSection icon={Target} title="Stage Change">
            <div className="flex items-center gap-2 mb-2">
              <StatusBadge status="progress" />
            </div>
            <p>
              Stage change trenutno menja vrednost opportunity-ja, ali backend još treba da pokrene
              odgovarajući workflow za kreiranje aktivnosti iz template-a.
            </p>
          </GuideSection>
        </>
      )}

      {infoTab === "campaigns" && (
        <>
          <GuideSection icon={ArrowUpRight} title="Campaign Tracking">
            <p>
              CRM sadrži i deo za marketing kampanje i tracking tokene. Ovaj modul služi za generisanje
              deljivih linkova i QR kodova koji vode na specifične marketing ili offer stranice.
            </p>
          </GuideSection>

          <GuideSection icon={FileText} title="Token Purpose">
            <p>
              Tracking token omogućava da se kasnije identifikuje izvor kampanje, kontakt ili ponuda
              sa koje je korisnik došao.
            </p>
          </GuideSection>
        </>
      )}

      {infoTab === "implemented" && (
        <>
          <GuideSection icon={CheckCircle2} title="Implemented Features">
            <div className="space-y-2">
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Party management</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Contacts management</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Addresses</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Channels</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Bank accounts</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Identifiers</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Lead creation and lead list</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Opportunity creation</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Opportunity list and stage update</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Manual activity creation</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Activity editing</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Global search</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="done" /><span>Campaign token generation</span></div>
            </div>
          </GuideSection>
        </>
      )}

      {infoTab === "roadmap" && (
        <>
          <GuideSection icon={Clock3} title="Roadmap / Next Steps">
            <div className="space-y-2">
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Automatic activity templates</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Workflow execution on opportunity stage change</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Role-based task dashboard</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Notifications for assigned tasks</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Opportunity timeline</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Offer to Project conversion</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Audit / history log</span></div>
              <div className="flex items-center gap-2"><StatusBadge status="planned" /><span>Advanced activity pagination / infinite loading</span></div>
            </div>
          </GuideSection>
        </>
      )}

      {infoTab === "rules" && (
        <>
          <GuideSection icon={ClipboardList} title="Functional Rules">
            <ul className="list-disc pl-5 space-y-1">
              <li>Opportunity must always be linked to a Party</li>
              <li>Global activity creation requires explicit Party selection</li>
              <li>Activity created from Party drawer must inherit that Party automatically</li>
              <li>Activity edit form must use update logic, not create logic</li>
              <li>Stage changes should eventually trigger workflow templates</li>
              <li>Lead should be convertible into Opportunity / Party flow</li>
            </ul>
          </GuideSection>

          <GuideSection icon={CircleHelp} title="Known Issues / Current Technical Caveats">
            <ul className="list-disc pl-5 space-y-1">
              <li>Some workflow automation paths are still being finalized</li>
              <li>Recent activity list is currently simplified and needs scalable loading</li>
              <li>Opportunity-driven activity generation is not yet fully connected</li>
            </ul>
          </GuideSection>
        </>
      )}

      {infoTab === "vision" && (
        <>
          <GuideSection icon={Briefcase} title="Future CRM Vision">
            <p>
              CRM treba da preraste iz osnovnog sales modula u centralni operativni sistem koji povezuje:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>sales</li>
              <li>design</li>
              <li>procurement</li>
              <li>transport</li>
              <li>assembly</li>
              <li>project management</li>
              <li>offer / contract / project lifecycle</li>
            </ul>
          </GuideSection>

          <GuideSection icon={Workflow} title="Long-Term Goal">
            <p>
              Dugoročni cilj je da CRM postane end-to-end workflow engine za ModularObjects, gde svaka
              promena statusa automatski proizvodi sledeće zadatke, odgovornosti, notifikacije i pregled
              stanja projekta.
            </p>
          </GuideSection>
        </>
      )}

      <div className="flex justify-end pt-2">
        
      </div>
    </div>
  </div>
</BigModal>

    </div>
  );
}

function GuideSection({ icon: Icon, title, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      <div className="text-sm leading-6 text-foreground/90">
        {children}
      </div>
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    done: "bg-green-100 text-green-700 border-green-200",
    progress: "bg-amber-100 text-amber-700 border-amber-200",
    planned: "bg-slate-100 text-slate-700 border-slate-200",
  };

  const label = {
    done: "Implemented",
    progress: "In progress",
    planned: "Planned",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${map[status] || map.planned}`}>
      {label[status] || label.planned}
    </span>
  );
}

function SettingsIcon() {
  // Minimalna ikona kao placeholder ako nemaš lucide Settings uvoz
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
      <path d="M19.14,12.94a7.09,7.09,0,0,0,0-1.88l2-1.55a.5.5,0,0,0,.12-.66l-1.9-3.29a.5.5,0,0,0-.61-.22l-2.35,1a7.19,7.19,0,0,0-1.63-.95l-.36-2.49A.5.5,0,0,0,14,1H10a.5.5,0,0,0-.5.42L9.14,3.91a7.19,7.19,0,0,0-1.63.95l-2.35-1a.5.5,0,0,0-.61.22L2.65,7.39a.5.5,0,0,0,.12.66l2,1.55a7.09,7.09,0,0,0,0,1.88l-2,1.55a.5.5,0,0,0-.12.66l1.9,3.29a.5.5,0,0,0,.61.22l2.35-1a7.19,7.19,0,0,0,1.63.95l.36,2.49A.5.5,0,0,0,10,23h4a.5.5,0,0,0,.5-.42l.36-2.49a7.19,7.19,0,0,0,1.63-.95l2.35,1a.5.5,0,0,0,.61-.22l1.9-3.29a.5.5,0,0,0-.12-.66ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z"/>
    </svg>
  );
}

function AddressesPanel({ party, items, refresh }) {
  const { t } = useTranslation();
  const [form, setForm] = React.useState({
    line1: "", line2: "", city: "", region: "", postCode: "",
    country: "RS", lat: "", lng: "", kind: "hq", isPrimary: false
  });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((a, idx) => (
  <div key={a.id ?? `addr-${party.id}-${idx}`} className="border rounded-md p-3 flex items-start justify-between gap-3">
    <div>
      <div className="font-medium">
        {a.line1 || "—"} {a.city ? `, ${a.city}` : ""} {a.country ? `, ${a.country}` : ""}
      </div>
      <div className="text-xs text-muted-foreground">
        {a.kind} {a.isPrimary ? "• primary" : ""} {a.postCode ? `• ${a.postCode}` : ""} {a.region ? `• ${a.region}` : ""}
      </div>
    </div>
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={!!a.isPrimary}
        onClick={async () => {
          try { await updateAddress(a.id, { isPrimary: 1 }); await refresh(); }
          catch (e) { alert(e.message); }
        }}
      >
        ★
      </Button>
      <Button
        variant="destructive"
        size="sm"
        onClick={async () => {
          if (!confirm(t("crm.common.delete"))) return;
          try { await deleteAddress(a.id); await refresh(); }
          catch (e) { alert(e.message); }
        }}
      >
        {t("crm.common.delete")}
      </Button>
    </div>
  </div>
))}
        {items.length===0 && <div className="text-sm text-muted-foreground">{t("crm.addresses.none")}</div>}
      </div>

      <form
        onSubmit={async (e)=>{ e.preventDefault();
          try{
            await createAddress(party.id,{
              line1:form.line1||null, line2:form.line2||null, city:form.city||null, region:form.region||null, postCode:form.postCode||null,
              country:form.country||null, lat:form.lat?Number(form.lat):null, lng:form.lng?Number(form.lng):null,
              kind:form.kind, isPrimary: !!form.isPrimary
            });
            setForm({ line1:"", line2:"", city:"", region:"", postCode:"", country:"RS", lat:"", lng:"", kind:"hq", isPrimary:false });
            await refresh();
          }catch(err){ alert(err.message); }
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <Input placeholder={t("crm.addresses.line1")} value={form.line1} onChange={e=>setForm({...form, line1:e.target.value})}/>
        <Input placeholder={t("crm.addresses.line2")} value={form.line2} onChange={e=>setForm({...form, line2:e.target.value})}/>
        <Input placeholder={t("crm.addresses.city")} value={form.city} onChange={e=>setForm({...form, city:e.target.value})}/>
        <Input placeholder={t("crm.addresses.region")} value={form.region} onChange={e=>setForm({...form, region:e.target.value})}/>
        <Input placeholder={t("crm.addresses.postCode")} value={form.postCode} onChange={e=>setForm({...form, postCode:e.target.value})}/>
        <Input placeholder={t("crm.addresses.country")} value={form.country} onChange={e=>setForm({...form, country:e.target.value})}/>
        <Input placeholder="Lat" value={form.lat} onChange={e=>setForm({...form, lat:e.target.value})}/>
        <Input placeholder="Lng" value={form.lng} onChange={e=>setForm({...form, lng:e.target.value})}/>
        <select className="h-9 rounded-md border px-2" value={form.kind} onChange={e=>setForm({...form, kind:e.target.value})}>
          <option value="hq">hq</option><option value="billing">billing</option>
          <option value="shipping">shipping</option><option value="site">site</option><option value="other">other</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPrimary} onChange={e=>setForm({...form, isPrimary:e.target.checked})}/>
          {t("crm.addresses.primary")}
        </label>
        <div className="md:col-span-2 flex justify-end"><Button type="submit">{t("crm.common.save")}</Button></div>
      </form>
    </div>
  );
}

function ChannelsPanel({ party, items, refresh }) {
  const { t } = useTranslation();
  const [form, setForm] = React.useState({ kind: "email", value: "", isPrimary: false });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((c, idx) => (
          <div key={c.id ?? `ch-${party.id}-${idx}`} className="border rounded-md p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">{c.value}</div>
              <div className="text-xs text-muted-foreground">
                {c.kind} {c.isPrimary ? "• primary" : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!!c.isPrimary}
                onClick={async () => {
                  try { await updateChannel(c.id, { isPrimary: 1 }); await refresh(); }
                  catch (e) { alert(e.message); }
                }}
              >
                ★
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={async () => {
                  if (!confirm(t("crm.common.delete"))) return;
                  try { await deleteChannel(c.id); await refresh(); }
                  catch (e) { alert(e.message); }
                }}
              >
                {t("crm.common.delete")}
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">{t("crm.channels.none")}</div>}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!form.value.trim()) return;
          try {
            await createChannel(party.id, { kind: form.kind, value: form.value, isPrimary: !!form.isPrimary });
            setForm({ kind: "email", value: "", isPrimary: false });
            await refresh();
          } catch (e) { alert(e.message); }
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <select className="h-9 rounded-md border px-2" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
          <option>email</option><option>phone</option><option>website</option>
          <option>linkedin</option><option>facebook</option><option>instagram</option>
          <option>x</option><option>youtube</option><option>tiktok</option>
          <option>github</option><option>other</option>
        </select>
        <Input placeholder={t("crm.channels.value")} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}/>
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.isPrimary} onChange={e => setForm({ ...form, isPrimary: e.target.checked })}/>
            {t("crm.channels.primary")}
          </label>
          <Button type="submit">{t("crm.common.save")}</Button>
        </div>
      </form>
    </div>
  );
}


function BankPanel({ party, items, refresh }) {
  const { t } = useTranslation();
  const [form, setForm] = React.useState({ bankName:"", accountNumber:"", iban:"", swift:"", currency:"", isPrimary:false });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((b, idx) => (
          <div key={b.id ?? `bank-${party.id}-${idx}`} className="border rounded-md p-3">
            <div className="font-medium">{b.bankName || "—"}</div>
            <div className="text-xs text-muted-foreground">
              {b.accountNumber || "—"} {b.iban ? `• ${b.iban}` : ""} {b.swift ? `• ${b.swift}` : ""} {b.currency ? `• ${b.currency}` : ""} {b.isPrimary ? "• primary" : ""}
            </div>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!!b.isPrimary}
                onClick={async () => { try { await updateBankAccount(b.id, { isPrimary: 1 }); await refresh(); } catch (e) { alert(e.message); } }}
              >★</Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={async () => {
                  if (!confirm(t("crm.common.delete"))) return;
                  try { await deleteBankAccount(b.id); await refresh(); }
                  catch (e) { alert(e.message); }
                }}
              >
                {t("crm.common.delete")}
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">{t("crm.bank.none")}</div>}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await createBankAccount(party.id, {
              bankName: form.bankName || null, accountNumber: form.accountNumber || null,
              iban: form.iban || null, swift: form.swift || null, currency: form.currency || null,
              isPrimary: !!form.isPrimary
            });
            setForm({ bankName:"", accountNumber:"", iban:"", swift:"", currency:"", isPrimary:false });
            await refresh();
          } catch (e) { alert(e.message); }
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <Input placeholder={t("crm.bank.bankName")} value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })}/>
        <Input placeholder={t("crm.bank.account")} value={form.accountNumber} onChange={e => setForm({ ...form, accountNumber: e.target.value })}/>
        <Input placeholder="IBAN" value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })}/>
        <Input placeholder="SWIFT" value={form.swift} onChange={e => setForm({ ...form, swift: e.target.value })}/>
        <Input placeholder={t("crm.bank.currency")} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}/>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPrimary} onChange={e => setForm({ ...form, isPrimary: e.target.checked })}/>
          {t("crm.bank.primary")}
        </label>
        <div className="md:col-span-3 flex justify-end"><Button type="submit">{t("crm.common.save")}</Button></div>
      </form>
    </div>
  );
}


function IdentifiersPanel({ party, items, refresh }) {
  const { t } = useTranslation();
  const [form, setForm] = React.useState({ kind:"PIB", value:"", issuerCountry:"RS", isPrimary:true });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((i, idx) => (
          <div key={i.id ?? `ident-${party.id}-${idx}`} className="border rounded-md p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">{i.value}</div>
              <div className="text-xs text-muted-foreground">
                {i.kind} {i.isPrimary ? "• primary" : ""} {i.issuerCountry ? `• ${i.issuerCountry}` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!!i.isPrimary}
                onClick={async () => { try { await updateIdentifier(i.id, { isPrimary: 1 }); await refresh(); } catch (e) { alert(e.message); } }}
              >★</Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={async () => {
                  if (!confirm(t("crm.common.delete"))) return;
                  try { await deleteIdentifier(i.id); await refresh(); }
                  catch (e) { alert(e.message); }
                }}
              >
                {t("crm.common.delete")}
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">{t("crm.identifiers.none")}</div>}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!form.value.trim()) return;
          try {
            await createIdentifier(party.id, {
              kind: form.kind, value: form.value, issuerCountry: form.issuerCountry || null, isPrimary: !!form.isPrimary
            });
            setForm({ kind:"PIB", value:"", issuerCountry:"RS", isPrimary:true });
            await refresh();
          } catch (e) { alert(e.message); }
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <select className="h-9 rounded-md border px-2" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
          <option value="PIB">PIB</option><option value="VAT">VAT</option>
          <option value="MB">MB</option><option value="Registration">Registration</option>
          <option value="Tax">Tax</option><option value="Other">Other</option>
        </select>
        <Input placeholder={t("crm.identifiers.value")} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}/>
        <Input placeholder={t("crm.identifiers.issuerCountry")} value={form.issuerCountry} onChange={e => setForm({ ...form, issuerCountry: e.target.value })}/>
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.isPrimary} onChange={e => setForm({ ...form, isPrimary: e.target.checked })}/>
            {t("crm.identifiers.primary")}
          </label>
          <Button type="submit">{t("crm.common.save")}</Button>
        </div>
      </form>
    </div>
  );
}

